import React, { useEffect, useState, useRef, useCallback, } from "react";
import uuid from "react-uuid";
import "./FroalaEditorStyles.scss";
import Froalaeditor from "froala-editor";
import { useSelector } from "react-redux";
import "froala-editor/js/plugins.pkgd.min.js";
import "froala-editor/css/froala_style.min.css";
import FroalaEditor from "react-froala-wysiwyg";
import EditOptionsDialog from "./EditOptionsDialog";
import "froala-editor/css/froala_editor.pkgd.min.css";
import BulbIcon from "../../../Assets/Images/bulbIcon.png";
import AddCircleOutlineOutlinedIcon from "@mui/icons-material/AddCircleOutlineOutlined";
import { Button, Popper, Grid, Paper, MenuItem, ClickAwayListener, CircularProgress } from "@mui/material";
import AICommentHelper from "../../Components/LineDetails/CommentDetails/AICommentHelper";
import CopySummaryModal from "../../Components/LineDetails/CommentDetails/CopySummaryModal";
import toast from "react-hot-toast";
import { origin } from "../../Config/Api-constants";
import axios from "axios";
import { Base64 } from "js-base64";
import MenuBookIcon from '@mui/icons-material/MenuBook';
import TextFieldsIcon from '@mui/icons-material/TextFields';

const getFilteredDropdownValues = (dropdown) =>
  dropdown?.dropdowns_values?.filter(obj => !obj?.toUpperCase()?.includes("XXX")) || [];

const normalizeApiDropdownOptions = (options) => (Array.isArray(options) ? options : []);

const mergeDropdownListsForLookup = (editorList, stateList) => {
  const normalizedEditor = Array.isArray(editorList) ? editorList : [];
  const normalizedState = Array.isArray(stateList) ? stateList : [];
  const byUuid = new Map(normalizedEditor.map((item) => [item?.dropdown_uuid, item]));
  normalizedState.forEach((item) => byUuid.set(item?.dropdown_uuid, item));
  return [...byUuid.values()].filter(Boolean);
};

// True when a node sits outside <li> and an ol/ul appears earlier among its preceding siblings
// (br, whitespace, text, outside-list wrappers, or other non-li elements may appear in between).
const isOutsideListAfterList = (node) => {
  if (!node || node.closest?.("li")) return false;
  let n = node;
  while (n) {
    if (n.matches?.("ol, ul")) return true;
    if (n.nodeType === Node.TEXT_NODE || n.nodeName === "BR") {
      n = n.previousSibling;
      continue;
    }
    if (n.classList?.contains?.("froala-dynamic-input-wrapper") && !n.closest("li")) {
      n = n.previousSibling;
      continue;
    }
    if (n.nodeType === Node.ELEMENT_NODE && !n.matches?.("ol, ul, li") && !n.closest?.("li")) {
      n = n.previousSibling;
      continue;
    }
    return false;
  }
  return false;
};

const getInsertAnchorNode = (range, editorRoot) => {
  if (!range) return null;
  const { startContainer, startOffset } = range;
  if (startContainer.nodeType === Node.TEXT_NODE) return startContainer;
  if (startContainer.nodeName === "BR") return startContainer;
  if (startContainer?.matches?.("ol, ul") && startOffset >= startContainer.childNodes.length) {
    return startContainer;
  }
  if (startContainer === editorRoot || startContainer?.classList?.contains?.("fr-element")) {
    return startOffset > 0 ? startContainer.childNodes[startOffset - 1] : null;
  }
  if (startContainer.nodeType === Node.ELEMENT_NODE) {
    if (startOffset > 0) return startContainer.childNodes[startOffset - 1];
    return startContainer.previousSibling || startContainer;
  }
  return null;
};

const isEffectivelyEmptyListItem = (listItem) =>
  !listItem?.innerHTML?.replace(/&nbsp;|<br\s*\/?>/gi, "").trim();

const listItemHasMeaningfulContent = (listItem) => {
  if (!listItem) return false;
  if (listItem.querySelector('.froala-dynamic-input-wrapper, span[id^="dropdown_"], select, input')) {
    return true;
  }
  return !isEffectivelyEmptyListItem(listItem);
};

const getOffsetAfterLeadingSeparators = (text) => {
  const match = (text || "").match(/^[\s\u00A0]*/);
  return match ? match[0].length : 0;
};

const getRangeAfterElement = (el) => {
  if (!el?.parentNode) return null;
  const afterRange = document.createRange();
  const nextSibling = el.nextSibling;
  if (nextSibling?.nodeType === Node.TEXT_NODE) {
    afterRange.setStart(nextSibling, getOffsetAfterLeadingSeparators(nextSibling.textContent));
  } else {
    afterRange.setStartAfter(el);
  }
  afterRange.collapse(true);
  return afterRange;
};

const normalizeRangeOutsideDynamicInputWrapper = (range, editorRoot) => {
  if (!range || !editorRoot) return range;
  const normalized = range.cloneRange();
  normalized.collapse(true);

  let contextEl =
    normalized.startContainer.nodeType === Node.TEXT_NODE
      ? normalized.startContainer.parentElement
      : normalized.startContainer;

  if (contextEl?.tagName === "INPUT") {
    contextEl = contextEl.closest(".froala-dynamic-input-wrapper") || contextEl;
  }

  const insideWrapper = contextEl?.closest?.(".froala-dynamic-input-wrapper");
  if (insideWrapper && editorRoot.contains(insideWrapper)) {
    return getRangeAfterElement(insideWrapper) || normalized;
  }

  return normalized;
};

const relocateDropdownOutsideDynamicInputWrapper = (dropdownEl) => {
  const dropdownWrapper =
    dropdownEl?.matches?.('span[id^="dropdown_"], .froala-dropdown-wrapper')
      ? dropdownEl
      : dropdownEl?.closest?.('span[id^="dropdown_"], .froala-dropdown-wrapper');
  if (!dropdownWrapper) return false;

  const dynamicWrapper = dropdownWrapper.closest(".froala-dynamic-input-wrapper");
  if (!dynamicWrapper?.parentNode) return false;

  const parent = dynamicWrapper.parentNode;
  parent.insertBefore(dropdownWrapper, dynamicWrapper.nextSibling);
  const next = dropdownWrapper.nextSibling;
  if (!(next?.nodeType === Node.TEXT_NODE && /^[\s\u00A0]*$/.test(next.textContent))) {
    parent.insertBefore(document.createTextNode("\u00A0"), dropdownWrapper.nextSibling);
  }
  return true;
};

const shouldInsertOutsideList = (editorRoot, range) => {
  if (!range?.collapsed || !editorRoot) return false;
  const { startContainer, startOffset } = range;

  // Caret in the list container after the final <li> — only when the list already
  // has meaningful content (not a brand-new single empty item).
  if (startContainer?.matches?.("ol, ul") && startOffset >= startContainer.childNodes.length) {
    const items = startContainer.querySelectorAll(":scope > li");
    return [...items].some(listItemHasMeaningfulContent);
  }

  let node = startContainer;
  if (node?.nodeType === Node.TEXT_NODE) node = node.parentElement;
  if (node?.closest?.("li")) return false;

  const anchor = getInsertAnchorNode(range, editorRoot);
  return anchor ? isOutsideListAfterList(anchor) : false;
};

const normalizeOutsideListInsertRange = (editorRoot, range) => {
  if (!range || !editorRoot || !shouldInsertOutsideList(editorRoot, range)) {
    return range?.cloneRange?.() || range;
  }

  const normalized = range.cloneRange();
  normalized.collapse(true);
  const { startContainer } = normalized;

  // Only snap when the caret is inside the list container (after the final <li>).
  // If the user is already outside among siblings after the list, keep their position.
  if (startContainer?.matches?.("ol, ul")) {
    const afterList = document.createRange();
    afterList.setStartAfter(startContainer);
    afterList.collapse(true);
    return afterList;
  }

  return normalizeRangeForLineBreakInsert(normalized);
};

// insertNode inserts before the range anchor; normalize so inserts land after <br> lines.
const normalizeRangeForLineBreakInsert = (range) => {
  if (!range) return range;
  const normalized = range.cloneRange();
  normalized.collapse(true);
  const { startContainer, startOffset } = normalized;

  if (startContainer.nodeName === "BR") {
    const afterBr = document.createRange();
    afterBr.setStartAfter(startContainer);
    afterBr.collapse(true);
    return afterBr;
  }

  if (startContainer.nodeType === Node.ELEMENT_NODE) {
    const atNode = startContainer.childNodes[startOffset];
    if (atNode?.nodeName === "BR") {
      let lastBr = atNode;
      while (lastBr.nextSibling?.nodeName === "BR") {
        lastBr = lastBr.nextSibling;
      }
      const afterBr = document.createRange();
      afterBr.setStartAfter(lastBr);
      afterBr.collapse(true);
      return afterBr;
    }
    if (startOffset > 0) {
      const prev = startContainer.childNodes[startOffset - 1];
      if (prev?.nodeName === "BR") {
        return normalized;
      }
    }
  }

  if (startContainer.nodeType === Node.TEXT_NODE && startOffset === 0) {
    const prev = startContainer.previousSibling;
    if (prev?.nodeName === "BR") {
      const afterBr = document.createRange();
      afterBr.setStartAfter(prev);
      afterBr.collapse(true);
      return afterBr;
    }
  }

  return normalized;
};

const collectBrChain = (startNode, direction) => {
  const brNodes = [];
  let node = startNode;
  while (node?.nodeName === "BR") {
    brNodes.push(node);
    node = direction === "prev" ? node.previousSibling : node.nextSibling;
  }
  return brNodes;
};

// Record <br> nodes already at the insertion point before the dynamic input wrapper is added.
const getPreservedBrNodesAtInsertPoint = (range) => {
  const preserved = new Set();
  if (!range?.collapsed) return preserved;

  const insertRange = normalizeRangeForLineBreakInsert(range.cloneRange());
  insertRange.collapse(true);
  const { startContainer, startOffset } = insertRange;

  if (startContainer.nodeType === Node.TEXT_NODE) {
    if (startOffset === 0) {
      collectBrChain(startContainer.previousSibling, "prev").forEach((br) => preserved.add(br));
    }
    if (startOffset >= (startContainer.textContent?.length || 0)) {
      collectBrChain(startContainer.nextSibling, "next").forEach((br) => preserved.add(br));
    }
  } else if (startContainer.nodeType === Node.ELEMENT_NODE) {
    if (startOffset > 0) {
      const prev = startContainer.childNodes[startOffset - 1];
      if (prev?.nodeName === "BR") {
        collectBrChain(prev, "prev").forEach((br) => preserved.add(br));
      }
    }
    if (startOffset < startContainer.childNodes.length) {
      const next = startContainer.childNodes[startOffset];
      if (next?.nodeName === "BR") {
        collectBrChain(next, "next").forEach((br) => preserved.add(br));
      }
    }
  }

  return preserved;
};

const isInsertSeparatorNode = (node) =>
  node?.nodeType === Node.TEXT_NODE && node.textContent === "\u00A0";

// Remove only Froala-injected <br> filler; keep user <br> that existed before insert.
const removeInjectedBrAdjacentToWrapper = (wrapper, preservedBrNodes) => {
  if (!wrapper?.parentNode) return false;
  const preserved = preservedBrNodes || new Set();
  let mutated = false;

  let prev = wrapper.previousSibling;
  while (prev?.nodeName === "BR") {
    if (preserved.has(prev)) break;
    const toRemove = prev;
    prev = prev.previousSibling;
    toRemove.remove();
    mutated = true;
  }

  let next = wrapper.nextSibling;
  if (isInsertSeparatorNode(next)) next = next.nextSibling;
  while (next?.nodeName === "BR") {
    if (preserved.has(next)) break;
    const toRemove = next;
    next = next.nextSibling;
    toRemove.remove();
    mutated = true;
  }

  return mutated;
};

const isRangeInEditor = (range, editorRoot) => {
  if (!range || !editorRoot) return false;
  const container = range.commonAncestorContainer;
  const el = container.nodeType === Node.TEXT_NODE ? container.parentNode : container;
  if (!el || !editorRoot.contains(el)) return false;
  if (el.tagName === "INPUT" || el.tagName === "SELECT") return false;
  if (isDynamicInputElement(el)) return false;
  return true;
};

const isDynamicInputElement = (el) =>
  !!el?.closest?.(".froala-dynamic-input-wrapper, span[id^='dropdown_']");

const NON_NARRATIVE_COMMENT_EDITOR_ID = "non-narrative-comment";

const isNonNarrativeCommentEditor = (editorHostId) =>
  editorHostId === NON_NARRATIVE_COMMENT_EDITOR_ID;

const isExternalModalFieldFocused = (editorRoot, editorHostId) => {
  if (!isNonNarrativeCommentEditor(editorHostId)) return false;
  if (document.activeElement?.id !== "commentTitle") return false;
  const active = document.activeElement;
  if (!active || active === document.body) return false;
  if (editorRoot?.contains(active)) return false;
  return true;
};

const setEditorHtml = (editorInstance, html) => {
  if (html === null || html === undefined) return;
  if (editorInstance?.html?.set) {
    editorInstance.html.set(html);
  }
};

const restoreExternalFocusAfter = (preserveEl, editorRoot, editorHostId) => {
  if (!preserveEl || !isNonNarrativeCommentEditor(editorHostId)) return;
  const attemptRestore = () => {
    const target = document.getElementById("commentTitle");
    if (!target || !document.contains(target)) return;
    if (document.activeElement === target) return;
    const active = document.activeElement;
    if (editorRoot?.contains(active)) {
      try {
        target.focus({ preventScroll: true });
      } catch (_) {}
    }
  };
  requestAnimationFrame(() => {
    attemptRestore();
    requestAnimationFrame(attemptRestore);
  });
};

const runHtmlSetPreservingExternalFocus = (editorInstance, html, editorHostId) => {
  if (!isNonNarrativeCommentEditor(editorHostId)) {
    setEditorHtml(editorInstance, html);
    return;
  }
  const root = editorInstance?.$el?.get(0);
  if (html === null || html === undefined || !root) return;
  const preserveEl = isExternalModalFieldFocused(root, editorHostId) ? document.activeElement : null;
  setEditorHtml(editorInstance, html);
  if (preserveEl) restoreExternalFocusAfter(preserveEl, root, editorHostId);
};

const isMeaningfulText = (text) => /[^\s\u00A0]/.test(text || "");

const isNodeBeforeWrapper = (node, wrapper) => {
  if (!node || !wrapper) return false;
  try {
    return !!(node.compareDocumentPosition(wrapper) & Node.DOCUMENT_POSITION_FOLLOWING);
  } catch (_) {
    return false;
  }
};

const isCaretInEditableTextBeforeWrapper = (range, wrapper) => {
  if (!range || !wrapper) return false;

  const { startContainer, startOffset } = range;

  if (startContainer.nodeType === Node.TEXT_NODE) {
    if (!isNodeBeforeWrapper(startContainer, wrapper)) return false;
    return isMeaningfulText(startContainer.textContent);
  }

  if (startContainer.nodeType === Node.ELEMENT_NODE) {
    const childAtOffset = startContainer.childNodes[startOffset];
    const childBeforeOffset =
      startOffset > 0 ? startContainer.childNodes[startOffset - 1] : null;

    for (const node of [childBeforeOffset, childAtOffset]) {
      if (!node || node === wrapper) continue;
      if (!isNodeBeforeWrapper(node, wrapper)) continue;
      if (node.nodeType === Node.TEXT_NODE && isMeaningfulText(node.textContent)) {
        return true;
      }
      if (node.nodeType === Node.ELEMENT_NODE && isMeaningfulText(node.textContent)) {
        return true;
      }
    }
  }

  return false;
};

const isClickOnTextBeforeWrapper = (probe, wrapper, li) => {
  if (!probe || !wrapper || !li) return false;

  let clickedEl = probe;
  if (clickedEl.nodeType === Node.TEXT_NODE) {
    clickedEl = clickedEl.parentElement;
  }
  if (!clickedEl || !li.contains(clickedEl)) return false;
  if (clickedEl.closest?.(".froala-dynamic-input-wrapper")) return false;
  if (!isNodeBeforeWrapper(clickedEl, wrapper)) return false;

  return isMeaningfulText(clickedEl.textContent);
};

const isInvalidEditorBlockElement = (el) =>
  !!el && (el.tagName === "UNDEFINED" || el.tagName === "undefined");

const unwrapInvalidEditorBlockElements = (editorRoot) => {
  if (!editorRoot) return false;
  let removed = false;
  let changed = true;
  while (changed) {
    changed = false;
    [...editorRoot.querySelectorAll("*")].forEach((el) => {
      if (!isInvalidEditorBlockElement(el)) return;
      const parent = el.parentNode;
      if (!parent) return;
      while (el.firstChild) {
        parent.insertBefore(el.firstChild, el);
      }
      el.remove();
      removed = true;
      changed = true;
    });
  }
  return removed;
};

const stripUndefinedTagsFromHtml = (html) =>
  html?.replace(/<\/?undefined\b[^>]*>/gi, "") ?? html;

const isFroalaEditorElement = (el) => !!el?.classList?.contains("fr-element");

const isFroalaOrphanBlockWrapper = (el) =>
  !!el &&
  !isFroalaEditorElement(el) &&
  (el.tagName === "P" || el.tagName === "DIV" || isInvalidEditorBlockElement(el));

const isEditorBlockEffectivelyEmpty = (el) => {
  if (!el) return false;
  const clone = el.cloneNode(true);
  clone.querySelectorAll(".froala-dynamic-input-wrapper, span[id^='dropdown_']").forEach((n) => n.remove());
  return !clone.innerHTML.replace(/&nbsp;|<br\s*\/?>/gi, "").trim();
};

const removeEmptyEditorBlocks = (editorRoot) => {
  if (!editorRoot) return false;
  let mutated = false;
  [...editorRoot.querySelectorAll("p, div")]
    .filter((el) => el !== editorRoot && !el.classList?.contains("fr-element"))
    .reverse()
    .forEach((el) => {
      if (el.querySelector(".froala-dynamic-input-wrapper, span[id^='dropdown_']")) return;
      if (isEditorBlockEffectivelyEmpty(el)) {
        el.remove();
        mutated = true;
      }
    });
  return mutated;
};

const isRemovableEmptyNodeAfterWrapper = (node) => {
  if (!node) return false;
  if (node.nodeName === "BR") return true;
  if (node.nodeType === Node.ELEMENT_NODE && (node.tagName === "DIV" || node.tagName === "P")) {
    return isEditorBlockEffectivelyEmpty(node);
  }
  return false;
};

const removeEmptyBlocksAfterDynamicInputs = (editorRoot) => {
  if (!editorRoot) return false;
  let mutated = false;

  editorRoot.querySelectorAll(".froala-dynamic-input-wrapper, span[id^='dropdown_']").forEach((wrapper) => {
    let next = wrapper.nextSibling;
    if (next?.nodeType === Node.TEXT_NODE && /^[\s\u00A0]*$/.test(next.textContent)) {
      next = next.nextSibling;
    }
    while (isRemovableEmptyNodeAfterWrapper(next)) {
      const toRemove = next;
      next = next.nextSibling;
      toRemove.remove();
      mutated = true;
    }
  });

  if (removeEmptyEditorBlocks(editorRoot)) mutated = true;
  return mutated;
};

const cleanupEmptyNodesBeforeWrapper = (wrapper, preservedBrNodes) => {
  if (!wrapper?.parentNode) return;
  const preserved = preservedBrNodes || new Set();
  let prev = wrapper.previousSibling;
  while (prev) {
    const toCheck = prev;
    prev = prev.previousSibling;
    if (toCheck.nodeName === "BR") {
      if (preserved.has(toCheck)) break;
      toCheck.remove();
      continue;
    }
    const isEmptyBlock =
      toCheck.nodeType === Node.ELEMENT_NODE &&
      (toCheck.tagName === "DIV" || toCheck.tagName === "P") &&
      isEditorBlockEffectivelyEmpty(toCheck);
    if (isEmptyBlock) {
      toCheck.remove();
      continue;
    }
    break;
  }
};

const finalizeControlWrapperInsert = (wrapper, preservedBrNodes = new Set()) => {
  if (!wrapper) return;
  cleanupEmptyNodesBeforeWrapper(wrapper, preservedBrNodes);
  removeInjectedBrAdjacentToWrapper(wrapper, preservedBrNodes);
};

const normalizeWrapperBlockToParagraph = (wrapper, editorRoot) => {
  if (!wrapper?.parentElement || wrapper.closest("li, ol, ul")) return;
  if (!editorRoot || !editorRoot.contains(wrapper)) return;

  let parent = wrapper.parentElement;
  while (
    parent &&
    parent !== editorRoot &&
    parent.tagName === "DIV" &&
    !isFroalaEditorElement(parent)
  ) {
    const grand = parent.parentElement;
    if (!grand || !editorRoot.contains(grand)) break;
    grand.insertBefore(wrapper, parent);
    if (isEditorBlockEffectivelyEmpty(parent)) {
      parent.remove();
    }
    parent = wrapper.parentElement;
  }

  parent = wrapper.parentElement;
  if (
    parent &&
    parent !== editorRoot &&
    parent.tagName === "DIV" &&
    !isFroalaEditorElement(parent)
  ) {
    const paragraph = document.createElement("p");
    while (parent.firstChild) {
      paragraph.appendChild(parent.firstChild);
    }
    parent.replaceWith(paragraph);
  }
};

const relocateDynamicInputsIntoEditorRoot = (editorRoot) => {
  if (!editorRoot || !isFroalaEditorElement(editorRoot)) return false;
  const host = editorRoot.parentElement;
  if (!host) return false;

  let mutated = false;
  [...host.childNodes].forEach((node) => {
    if (node.nodeType !== Node.ELEMENT_NODE) return;
    const isWrapper =
      node.classList?.contains("froala-dynamic-input-wrapper") ||
      node.matches?.("span[id^='dropdown_']");
    if (!isWrapper) return;

    let paragraph = editorRoot.querySelector("p:last-child") || editorRoot.querySelector("p");
    if (!paragraph) {
      paragraph = document.createElement("p");
      paragraph.innerHTML = "<br>";
      editorRoot.appendChild(paragraph);
    }

    const trailing = node.nextSibling;
    paragraph.appendChild(node);
    if (
      trailing?.nodeType === Node.TEXT_NODE &&
      /^[\s\u00A0]*$/.test(trailing.textContent)
    ) {
      paragraph.appendChild(trailing);
    }
    mutated = true;
  });

  return mutated;
};

const ensureParagraphContextForInsert = (editorInstance, editorRoot) => {
  if (!editorInstance || !editorRoot) return;
  removeEmptyEditorBlocks(editorRoot);

  const hasControls = editorRoot.querySelector(
    ".froala-dynamic-input-wrapper, span[id^='dropdown_']"
  );
  if (hasControls) return;

  const hasText = editorRoot.textContent?.replace(/\u00A0/g, " ").trim();
  if (hasText) return;

  const usesDivBlocks = [...editorRoot.children].some((child) => child.tagName === "DIV");
  const hasEmptyStructure =
    [...editorRoot.children].length === 0 ||
    [...editorRoot.children].every(isEditorBlockEffectivelyEmpty);

  if (usesDivBlocks || hasEmptyStructure) {
    editorInstance.html.set("<p><br></p>");
    const paragraph = editorRoot.querySelector("p");
    if (paragraph) {
      try {
        editorInstance.selection.setAtEnd(paragraph);
      } catch (_) {}
    }
  }
};

const getAncestorFromRange = (range, selector) => {
  if (!range) return null;
  let node = range.startContainer;
  if (node?.nodeType === Node.TEXT_NODE) node = node.parentElement;
  return node?.closest?.(selector) || null;
};

const getListItemFromRange = (range) => getAncestorFromRange(range, "li");

const FroalaEditorComponent = (props) => {
  const {
    id,
    type,
    showAI,
    editor,
    onChange,
    dialogRef,
    setCopied,
    isFocused,
    editorData,
    showAIModal,
    commentData,
    categoryName,
    commentTitle,
    froalaConfig,
    setIsFocused,
    setHasChanges,
    setShowAIModal,
    setCommentText,
    setCommentSummary,
    setEditorContent,
    setIsButtonAdded,
    handleDropdownList,
    categoryNameFromLocation,
    templateId,
    handleIntroSave,
    hasChanges,
    attachDropdownListenersInsideFroala,
    onFocusEditor,
    onBlurEditor,
    setActiveEditorId,
    filterTagList,
    categoryId,
    introductoryTextMedia,
    onLocalEditStart
  } = props;
  
  const normalizeDropdowns = (raw) => {
    if (Array.isArray(raw)) return raw;
    if (Array.isArray(raw?.introductory_text_dropdowns)) return raw.introductory_text_dropdowns;
    return [];
  };
  
  const storeData = useSelector((state) => state.CommonReducer);
  const rwSettingAccess = storeData?.inspectionData?.data?.REPORT_WRITER_SETTINGS_ACCESS;
  const [state, setState] = useState({
    open: false,
    globalListsFetchNonce: 0,
    openHelp: false,
    editedOption: null,
    dialogOptionList: [],
    isdropdownAdded: true,
    selectedDropdown: null,
    openRemoveDropdown: false,
    dropdownList: id === "intro-text-editor" ? commentData?.introductory_text_dropdowns : id === "commentText" || id === "non-narrative-comment" ? commentData?.comment_dropdowns : commentData?.summary_comment_dropdowns,
    selectedInput: null,
    summaryOptionList: [],
    name: "", tags: storeData.defaultCategories || [], description: "", saveToGlobalLists: false, listId: null,
  });

  const [anchorEl, setAnchorEl] = useState(null);
  const [currentInputId, setCurrentInputId] = useState(null);
  const [dropdownOptions, setDropdownOptions] = useState([]);
  const [isDropdownLoading, setIsDropdownLoading] = useState(false);
  const instanceId = useRef(`froala-${id}-${Date.now()}`);
  const initTimeoutRef = useRef(null);
  const editorDataRef = useRef(editorData);
  const lastProcessedIntroTextRef = useRef(null);
  const prevIntroCategoryIdRef = useRef(categoryId);
  const savedRangeRef = useRef(null);
  const hadTextSelectionOnMouseupRef = useRef(false);
  const addInputClickRangeRef = useRef(null);
  const lastFocusedDropdownRef = useRef(null);
  const lastFocusedDynamicWrapperRef = useRef(null);
  // Tracks whether a dropdown value is being selected so the input's blur handler
  // doesn't overwrite the chosen value with the previously typed text.
  const suppressBlurPersistRef = useRef(false);
  const anchorElRef = useRef(null);
  const dynamicInputListenersRef = useRef(new Map());
  const dynamicInputObserverRef = useRef(null);
  const dynamicInputObserverRootRef = useRef(null);
  const suppressModelChangeRef = useRef(false);
  const contentChangedNormalizingRef = useRef(false);
  const froalaEditorConfigRef = useRef(null);
  const stateRef = useRef(state);
  const dropdownOpenRequestRef = useRef(0);

  const syncDynamicInputValuesToAttributes = useCallback((editorRoot) => {
    const root = editorRoot || editor.current?.editor?.$el?.get(0);
    if (!root) return;
    root.querySelectorAll(".froala-dynamic-input-wrapper input").forEach((input) => {
      const val = input.value.replace(/"/g, "&quot;");
      input.setAttribute("value", val);
      input.setAttribute("title", val);
    });
  }, [editor]);

  const detachDynamicInputListeners = useCallback((inputId) => {
    const entry = dynamicInputListenersRef.current.get(inputId);
    if (!entry) return;
    entry.inputEl.removeEventListener("change", entry.handleChange);
    entry.inputEl.removeEventListener("input", entry.handleInput);
    entry.inputEl.removeEventListener("keydown", entry.handleKeydown);
    entry.inputEl.removeEventListener("keypress", entry.handleKeypress);
    if (entry.handleMousedown) entry.inputEl.removeEventListener("mousedown", entry.handleMousedown, true);
    if (entry.handleMouseup) entry.inputEl.removeEventListener("mouseup", entry.handleMouseup, true);
    if (entry.handleClick) entry.inputEl.removeEventListener("click", entry.handleClick, true);
    if (entry.handleWrapperMouseup && entry.wrapper) {
      entry.wrapper.removeEventListener("mouseup", entry.handleWrapperMouseup, true);
    }
    if (entry.handleWrapperMousedown && entry.wrapper) {
      entry.wrapper.removeEventListener("mousedown", entry.handleWrapperMousedown, true);
    }
    if (entry.handleClick && entry.wrapper) {
      entry.wrapper.removeEventListener("click", entry.handleClick, true);
    }
    if (entry.handleFocus) entry.inputEl.removeEventListener("focus", entry.handleFocus);
    if (entry.handleBlur) entry.inputEl.removeEventListener("blur", entry.handleBlur);
    dynamicInputListenersRef.current.delete(inputId);
  }, []);

  // Remove any `.froala-dynamic-input-wrapper` spans that no longer contain an <input>.
  // Covers cases where the input is deleted via selection/cut/undo, leaving empty or
  // nested wrappers behind. Returns true if anything was removed.
  const removeEmptyDynamicInputWrappers = useCallback((root) => {
    const editorRoot = root || editor.current?.editor?.$el?.get(0);
    if (!editorRoot) return false;
    const wrappers = editorRoot.querySelectorAll(".froala-dynamic-input-wrapper");
    let removed = false;
    wrappers.forEach((wrapper) => {
      if (wrapper.querySelector("input")) return;
      wrapper.remove();
      removed = true;
    });
    return removed;
  }, [editor]);

  const removeInvalidEditorBlockElements = useCallback((root) => {
    const editorRoot = root || editor.current?.editor?.$el?.get(0);
    if (!editorRoot) return false;
    return unwrapInvalidEditorBlockElements(editorRoot);
  }, [editor]);

  const cleanupRemovedDynamicInputs = useCallback((mutations) => {
    const hasRemovals = mutations.some((mutation) => mutation.removedNodes.length > 0);
    if (!hasRemovals) return;
    if (dynamicInputListenersRef.current.size) {
      [...dynamicInputListenersRef.current.keys()].forEach((inputId) => {
        const entry = dynamicInputListenersRef.current.get(inputId);
        if (entry && !document.contains(entry.inputEl)) {
          detachDynamicInputListeners(inputId);
        }
      });
    }
    // Strip wrapper spans left behind once their input is gone.
    removeEmptyDynamicInputWrappers(dynamicInputObserverRootRef.current);
    removeInvalidEditorBlockElements(dynamicInputObserverRootRef.current);
  }, [detachDynamicInputListeners, removeEmptyDynamicInputWrappers, removeInvalidEditorBlockElements]);

  const ensureDynamicInputObserver = useCallback((editorRoot) => {
    if (!editorRoot) return;
    if (dynamicInputObserverRef.current && dynamicInputObserverRootRef.current === editorRoot) return;
    dynamicInputObserverRef.current?.disconnect();
    dynamicInputObserverRef.current = new MutationObserver(cleanupRemovedDynamicInputs);
    dynamicInputObserverRef.current.observe(editorRoot, { childList: true, subtree: true });
    dynamicInputObserverRootRef.current = editorRoot;
  }, [cleanupRemovedDynamicInputs]);

  const buildRangeAfterWrapper = useCallback((wrapper) => {
    if (!wrapper?.parentNode || !document.contains(wrapper)) return null;
    let next = wrapper.nextSibling;
    if (isInsertSeparatorNode(next)) {
      next = next.nextSibling;
    }
    const range = document.createRange();
    if (next?.nodeType === Node.TEXT_NODE && next.textContent?.replace(/[\s\u00A0]/g, "")) {
      range.setStart(next, getOffsetAfterLeadingSeparators(next.textContent));
    } else {
      const anchor = isInsertSeparatorNode(wrapper.nextSibling) ? wrapper.nextSibling : wrapper;
      range.setStartAfter(anchor);
    }
    range.collapse(true);
    return range;
  }, []);

  const registerDynamicInputListeners = useCallback((inputEl, inputId, handleKeydown, handleChange) => {
    detachDynamicInputListeners(inputId);
    const syncInputAttributes = () => {
      const val = inputEl.value.replace(/"/g, "&quot;");
      inputEl.setAttribute("value", val);
      inputEl.setAttribute("title", val);
    };
    const handleInput = (event) => {
      event.stopPropagation();
      syncInputAttributes();
    };
    const handleKeydownWrapped = (event) => {
      event.stopPropagation();
      handleKeydown(event);
    };
    const handleKeypress = (event) => {
      event.stopPropagation();
    };
    const handleMousedown = (event) => {
      event.stopPropagation();
    };
    const wrapper = inputEl.closest(".froala-dynamic-input-wrapper");
    const handleWrapperMousedown = (event) => {
      event.stopPropagation();
    };
    const handleMouseup = (event) => {
      event.stopPropagation();
    };
    const handleWrapperMouseup = (event) => {
      event.stopPropagation();
    };
    const handleClick = (event) => {
      event.stopPropagation();
    };
    const handleFocus = () => {
      const wrapper = inputEl.closest(".froala-dynamic-input-wrapper");
      lastFocusedDynamicWrapperRef.current = wrapper;
    };
    const handleBlur = (event) => {
      syncInputAttributes();
      const wrapper = inputEl.closest(".froala-dynamic-input-wrapper");
      if (wrapper?.parentNode && document.contains(wrapper)) {
        // Always track this wrapper so the next "Add input" inserts after it.
        lastFocusedDynamicWrapperRef.current = wrapper;
        // Update savedRangeRef only if focus is NOT moving to the "Add input" button
        // (in which case addInput will handle range setup via anchorWrapper logic).
        if (!event.relatedTarget?.id?.startsWith("add-input")) {
          try { savedRangeRef.current = buildRangeAfterWrapper(wrapper); } catch (_) {}
        }
      }
      const editorRoot = editor.current?.editor?.$el?.get(0);
      if (editorRoot) {
        let domMutated = false;
        if (relocateDynamicInputsIntoEditorRoot(editorRoot)) domMutated = true;
        if (removeEmptyBlocksAfterDynamicInputs(editorRoot)) domMutated = true;
        if (domMutated) {
          editor.current?.editor?.events?.trigger("contentChanged");
        }
      }
    };
    dynamicInputListenersRef.current.set(inputId, {
      inputEl,
      handleChange,
      handleInput,
      handleKeydown: handleKeydownWrapped,
      handleKeypress,
      handleMousedown,
      handleMouseup,
      handleWrapperMousedown,
      handleWrapperMouseup,
      handleClick,
      wrapper,
      handleFocus,
      handleBlur,
    });
    inputEl.addEventListener("change", handleChange);
    inputEl.addEventListener("input", handleInput);
    inputEl.addEventListener("keydown", handleKeydownWrapped);
    inputEl.addEventListener("keypress", handleKeypress);
    inputEl.addEventListener("mousedown", handleMousedown, true);
    inputEl.addEventListener("mouseup", handleMouseup, true);
    inputEl.addEventListener("click", handleClick, true);
    if (wrapper) {
      wrapper.addEventListener("mousedown", handleWrapperMousedown, true);
      wrapper.addEventListener("mouseup", handleWrapperMouseup, true);
      wrapper.addEventListener("click", handleClick, true);
    }
    inputEl.addEventListener("focus", handleFocus);
    inputEl.addEventListener("blur", handleBlur);
  }, [detachDynamicInputListeners, buildRangeAfterWrapper]);

  // If `range` starts at or before a .froala-dynamic-input-wrapper inside a <li>,
  // move the caret to immediately after that wrapper. Called from mouseup and click
  // events to prevent the caret from landing before the input when clicking next to it.
  const correctCaretBeforeWrapper = (sel, range, editorRoot, editorInstance, eventTarget, clientX, clientY) => {
    let probe = eventTarget;
    if (!isDynamicInputElement(probe) && clientX != null && clientY != null) {
      probe = document.elementFromPoint(clientX, clientY) || probe;
    }
    if (isDynamicInputElement(probe)) return;
    if (isDynamicInputElement(document.activeElement)) return;

    const startContainer = range.startContainer;
    const startOffset = range.startOffset;

    // Resolve to an element.
    const startEl =
      startContainer.nodeType === Node.TEXT_NODE
        ? startContainer.parentElement
        : startContainer;

    // Primary path: caret is already inside a <li>.
    let li = startEl?.closest?.("li");

    // Fallback 1: caret is directly in an <ol> or <ul> (focus was routed through
    // the list container when clicking next to the input). Only look at direct <li>
    // children — do NOT descend into nested <ol>/<ul> via querySelector, otherwise
    // clicking anywhere that puts the caret in the editor root <div> would resolve
    // to the first <li> and incorrectly move the cursor to the first input.
    if (!li && (startEl?.tagName === "OL" || startEl?.tagName === "UL")) {
      const toDirectLi = (node) => (node?.tagName === "LI" ? node : null);
      li =
        toDirectLi(startEl.childNodes[startOffset]) ||
        (startOffset > 0 ? toDirectLi(startEl.childNodes[startOffset - 1]) : null);
    }

    // Fallback 2: if still no <li>, check if startContainer is a text node inside <ol>/<ul>
    // (sometimes the caret ends up in whitespace text node within the list).
    if (!li && startContainer.nodeType === Node.TEXT_NODE) {
      const listParent = startContainer.parentElement?.closest?.("ol, ul");
      if (listParent) {
        // Find the closest <li> that contains or is adjacent to this text node.
        li = startContainer.parentElement?.closest?.("li");
        if (!li) {
          // Text node is a direct child of ol/ul; find the last li.
          const lis = listParent.querySelectorAll(":scope > li");
          if (lis.length > 0) li = lis[lis.length - 1];
        }
      }
    }

    if (!li) return;

    // Find the wrapper that the caret is closest to (before or at its position).
    // This handles cases with multiple inputs in the same list item.
    const wrappers = li.querySelectorAll(".froala-dynamic-input-wrapper");
    if (wrappers.length === 0) return;

    let targetWrapper = null;
    for (const w of wrappers) {
      try {
        const beforeW = document.createRange();
        beforeW.setStartBefore(w);
        beforeW.collapse(true);
        // If the caret is before (or exactly at) this wrapper, it's our target.
        if (range.compareBoundaryPoints(Range.START_TO_START, beforeW) <= 0) {
          targetWrapper = w;
          break;
        }
      } catch (_) {}
    }

    if (!targetWrapper) return;

    if (
      isCaretInEditableTextBeforeWrapper(range, targetWrapper) ||
      isClickOnTextBeforeWrapper(eventTarget, targetWrapper, li)
    ) {
      return;
    }

    try {
      const beforeWrapper = document.createRange();
      beforeWrapper.setStartBefore(targetWrapper);
      beforeWrapper.collapse(true);
      // Only correct if the caret is before (or exactly at) the wrapper's start.
      if (range.compareBoundaryPoints(Range.START_TO_START, beforeWrapper) > 0) return;
      const corrected = buildRangeAfterWrapper(targetWrapper);
      if (!corrected) return;
      sel.removeAllRanges();
      sel.addRange(corrected);
      savedRangeRef.current = corrected.cloneRange();

      // Keep focus on a dynamic input the user clicked; only refocus the editor otherwise.
      if (
        editorInstance?.$el &&
        !isDynamicInputElement(document.activeElement)
      ) {
        const root = editorInstance.$el.get(0);
        if (
          !isNonNarrativeCommentEditor(id) ||
          !isExternalModalFieldFocused(root, id)
        ) {
          try {
            root.focus();
          } catch (_) {}
        }
      }
    } catch (_) {}
  };

  // After a dynamic input/wrapper is removed via Backspace, return focus to the editor
  // and collapse the caret exactly where the wrapper was so movement stays smooth.
  const placeCaretInEditorAt = useCallback((anchorNode, parentNode) => {
    const activeEditor = editor.current?.editor;
    if (!activeEditor) return;
    try {
      activeEditor.events.focus();
    } catch (err) {}
    const sel = window.getSelection();
    if (!sel) return;
    const newRange = document.createRange();
    if (anchorNode && document.contains(anchorNode)) {
      if (anchorNode.nodeType === Node.TEXT_NODE) {
        newRange.setStart(anchorNode, anchorNode.textContent.length);
      } else {
        newRange.setStartAfter(anchorNode);
      }
    } else if (parentNode && document.contains(parentNode)) {
      newRange.setStart(parentNode, 0);
    } else {
      return;
    }
    newRange.collapse(true);
    sel.removeAllRanges();
    sel.addRange(newRange);
    savedRangeRef.current = newRange.cloneRange();
  }, [editor]);

  // Strip only duplicate regular-whitespace nodes that Froala piles up after each dynamic
  // input wrapper. Preserve the auto-inserted &nbsp; separator and any user-typed nbsp.
  const normalizeDynamicInputSeparators = useCallback((editorRoot) => {
    if (!editorRoot) return false;
    let mutated = false;
    const wrappers = editorRoot.querySelectorAll(".froala-dynamic-input-wrapper");
    wrappers.forEach((wrapper) => {
      let next = wrapper.nextSibling;
      if (isInsertSeparatorNode(next)) {
        next = next.nextSibling;
      } else if (next?.nodeType === Node.TEXT_NODE && /\u00A0/.test(next.textContent)) {
        next = next.nextSibling;
      }

      let keptOneRegularWhitespace = false;
      while (next && next.nodeType === Node.TEXT_NODE && /^[ \t\n\r\f\v]*$/.test(next.textContent)) {
        if (!keptOneRegularWhitespace && next.textContent.length > 0) {
          keptOneRegularWhitespace = true;
          next = next.nextSibling;
          continue;
        }
        const toRemove = next;
        next = next.nextSibling;
        toRemove.remove();
        mutated = true;
      }
    });
    return mutated;
  }, []);

  const attachMissingDynamicInputListeners = useCallback((editorRoot) => {
    const root = editorRoot || editor.current?.editor?.$el?.get(0);
    if (!root) return;
    root.querySelectorAll(".froala-dynamic-input-wrapper input[id]").forEach((inputEl) => {
      const inputId = inputEl.id;
      if (dynamicInputListenersRef.current.has(inputId)) return;

      const handleChange = (event) => {
        event.stopPropagation();
        const val = event.target.value.replace(/"/g, "&quot;").trim();
        event.target.setAttribute("value", val);
        event.target.setAttribute("title", val);
        setHasChanges?.(true);
        const wrapper = inputEl.closest(".froala-dynamic-input-wrapper");
        if (wrapper) {
          lastFocusedDynamicWrapperRef.current = wrapper;
        }
        document.dispatchEvent(
          new CustomEvent("changeInput", {
            detail: { id: inputId, value: event.target.value },
          })
        );
      };

      const handleKeydown = (keyEvent) => {
        if (keyEvent.key === "Backspace" && inputEl.value === "") {
          keyEvent.preventDefault();
          const wrapper = inputEl.closest(".froala-dynamic-input-wrapper");
          if (wrapper) {
            detachDynamicInputListeners(inputId);
            const anchorNode = wrapper.previousSibling;
            const parentNode = wrapper.parentNode;
            wrapper.remove();
            const activeEditor = editor.current?.editor;
            if (activeEditor) {
              normalizeDynamicInputSeparators(activeEditor.$el?.get(0));
              placeCaretInEditorAt(anchorNode, parentNode);
              activeEditor.events.trigger("contentChanged");
              const updatedContent = activeEditor.html.get();
              if (setEditorContent) setEditorContent(prev => ({ ...prev, innerHTML: updatedContent }));
              else if (id === "commentText" && setCommentText) setCommentText(prev => ({ ...prev, innerHTML: updatedContent }));
              else if (id === "summaryText" && setCommentSummary) setCommentSummary(prev => ({ ...prev, innerHTML: updatedContent }));
            }
          }
        }
      };

      registerDynamicInputListeners(inputEl, inputId, handleKeydown, handleChange);
    });
  }, [
    editor,
    buildRangeAfterWrapper,
    detachDynamicInputListeners,
    registerDynamicInputListeners,
    normalizeDynamicInputSeparators,
    placeCaretInEditorAt,
    setHasChanges,
    setEditorContent,
    setCommentText,
    setCommentSummary,
    id,
  ]);

  const isListItemEffectivelyEmpty = (listItem) =>
    !listItem?.innerHTML?.replace(/&nbsp;|<br\s*\/?>/gi, "").trim();

  const listItemFromListElement = (listEl) => {
    if (!listEl?.matches?.("ol, ul")) return null;
    const items = listEl.querySelectorAll(":scope > li");
    if (!items.length) return null;
    const emptyLi = [...items].reverse().find(isListItemEffectivelyEmpty);
    return emptyLi || items[items.length - 1];
  };

  // Froala often leaves the caret as a sibling of <ol>/<ul> (not inside <li>) on empty lists.
  const resolveTargetListItem = (editorRoot, range, editorInstance) => {
    if (!range || !editorRoot) return null;
    if (shouldInsertOutsideList(editorRoot, range)) return null;

    const fromRange = getListItemFromRange(range);
    if (fromRange && editorRoot?.contains(fromRange)) return fromRange;

    if (!isRangeInEditor(range, editorRoot)) return null;

    const anchorNode = getInsertAnchorNode(range, editorRoot);
    if (anchorNode && isOutsideListAfterList(anchorNode)) return null;

    try {
      const selEl = editorInstance?.selection?.element?.();
      const fromSelection = selEl?.closest?.("li");
      if (fromSelection && editorRoot?.contains(fromSelection)) {
        if (shouldInsertOutsideList(editorRoot, range)) return null;
        return fromSelection;
      }
    } catch (_) {}

    const { startContainer, startOffset } = range;
    const tryAdjacentList = (node) => listItemFromListElement(node);

    if (startContainer === editorRoot || startContainer?.classList?.contains?.("fr-element")) {
      const prev = startOffset > 0 ? startContainer.childNodes[startOffset - 1] : null;
      if (prev && isOutsideListAfterList(prev)) return null;
      return (
        tryAdjacentList(startContainer.childNodes[startOffset - 1]) ||
        tryAdjacentList(startContainer.childNodes[startOffset])
      );
    }

    if (startContainer.nodeType === Node.ELEMENT_NODE) {
      const fromChild = tryAdjacentList(startContainer.childNodes[startOffset - 1]);
      if (fromChild) return fromChild;
    }

    if (startContainer.nodeType === Node.TEXT_NODE) {
      if (isOutsideListAfterList(startContainer)) return null;
      let sib = startContainer.previousSibling;
      while (sib) {
        const li = tryAdjacentList(sib);
        if (li) return li;
        if (sib.nodeType === Node.ELEMENT_NODE && !sib.matches?.("ol, ul")) break;
        sib = sib.previousSibling;
      }
    }

    return null;
  };

  const isNodePositionAfterList = (editorRoot, node) =>
    !!node && !!editorRoot?.contains(node) && isOutsideListAfterList(node);

  const isCaretAfterList = (editorRoot, range) => shouldInsertOutsideList(editorRoot, range);

  const insertDynamicInputAtRange = (wrapperId, range, editorInstance, preserveAdjacentBr = true) => {
    const preservedBrNodes = preserveAdjacentBr ? getPreservedBrNodesAtInsertPoint(range) : new Set();
    const temp = document.createElement("div");
    temp.innerHTML = buildDynamicInputWrapperHtml(wrapperId);
    const wrapperEl = temp.firstElementChild;
    const spaceNode = document.createTextNode("\u00A0");
    const insertRange = normalizeRangeForLineBreakInsert(range);
    insertRange.insertNode(wrapperEl);
    insertRange.setStartAfter(wrapperEl);
    insertRange.collapse(true);
    insertRange.insertNode(spaceNode);
    editorInstance.events.trigger("contentChanged");
    finalizeControlWrapperInsert(wrapperEl, preservedBrNodes);
    return wrapperEl;
  };

  const buildDynamicInputWrapperHtml = (inputId) =>
    `<span contenteditable="false" class="froala-dynamic-input-wrapper"><input id="${inputId}" type="text" value="" title="" style="width:104px;background-color:#f1f9fe;height:24px !important;overflow-wrap:break-word;padding:1px 17px 3px 6px;border-radius:4px;border:1px solid #0F75BC;appearance:none;-webkit-appearance:none;-moz-appearance:none;font-size:14px;color:#3B3B3B;font-style:normal;font-weight:600;max-width:200px;min-width:104px;text-overflow:ellipsis;margin-left:2px;white-space:nowrap;"/></span>`;

  const insertDynamicInputInListItem = (listItem, inputId, insertRange, editorInstance, preserveAdjacentBr = true) => {
    const preservedBrNodes =
      preserveAdjacentBr && insertRange ? getPreservedBrNodesAtInsertPoint(insertRange) : new Set();
    const temp = document.createElement("div");
    temp.innerHTML = buildDynamicInputWrapperHtml(inputId);
    const wrapperEl = temp.firstElementChild;

    if (isListItemEffectivelyEmpty(listItem)) {
      listItem.innerHTML = "";
    }

    const canInsertAtRange =
      insertRange &&
      document.contains(insertRange.startContainer) &&
      listItem.contains(insertRange.startContainer);

    if (canInsertAtRange) {
      const insertAt = normalizeRangeForLineBreakInsert(insertRange);
      insertAt.insertNode(wrapperEl);
    } else {
      const lastWrapperInLi = listItem.querySelector(".froala-dynamic-input-wrapper:last-of-type");
      if (lastWrapperInLi?.parentNode) {
        const parent = lastWrapperInLi.parentNode;
        const nextSib = lastWrapperInLi.nextSibling;
        parent.insertBefore(wrapperEl, nextSib);
      } else {
        listItem.appendChild(wrapperEl);
      }
    }
    editorInstance.events.trigger("contentChanged");
    finalizeControlWrapperInsert(wrapperEl, preservedBrNodes);
    return wrapperEl;
  };

  const relocateWrapperIntoAdjacentList = (wrapper, editorRoot) => {
    if (!wrapper || !editorRoot || wrapper.closest("li")) return false;
    if (isNodePositionAfterList(editorRoot, wrapper)) return false;

    let listEl = null;
    let sib = wrapper.previousSibling;
    while (sib) {
      if (sib.nodeType === Node.ELEMENT_NODE && (sib.matches("ol") || sib.matches("ul"))) {
        listEl = sib;
        break;
      }
      sib = sib.previousSibling;
    }

    const listItem = listItemFromListElement(listEl);
    if (!listItem) return false;

    if (isListItemEffectivelyEmpty(listItem)) {
      listItem.innerHTML = "";
    }

    listItem.appendChild(wrapper);
    const next = wrapper.nextSibling;
    if (!(next?.nodeType === Node.TEXT_NODE && /^[\s\u00A0]*$/.test(next.textContent))) {
      wrapper.after(document.createTextNode("\u00A0"));
    }
    return true;
  };

  useEffect(() => {
    return () => {
      dynamicInputObserverRef.current?.disconnect();
      dynamicInputObserverRef.current = null;
      dynamicInputObserverRootRef.current = null;
      [...dynamicInputListenersRef.current.keys()].forEach(detachDynamicInputListeners);
    };
  }, [detachDynamicInputListeners]);

  useEffect(() => {
    editorDataRef.current = editorData;
  }, [editorData]);

  // Removed: Sync is handled by the initialization effect which properly checks dependencies

  useEffect(() => {
    anchorElRef.current = anchorEl;
  }, [anchorEl]);

  useEffect(() => {
    stateRef.current = state;
  }, [state]);

  useEffect(() => {
    const handleGlobalDropdownOpen = (event) => {
      if (event.detail.instanceId !== instanceId.current) {
        setAnchorEl(null);
        setCurrentInputId(null);
      }
    };
    window.addEventListener('froala-dropdown-open', handleGlobalDropdownOpen);
    return () => window.removeEventListener('froala-dropdown-open', handleGlobalDropdownOpen);
  }, []);

  const { open, openHelp, dropdownList, selectedDropdown, dialogOptionList, openRemoveDropdown, summaryOptionList, saveToGlobalLists, name, description, tags = [], listGuid } = state;
  const templateData = useSelector((state) => state.CommonReducer)?.inspectionData?.data?.inspectiontemplates;
  const templateIndex = templateData?.findIndex((itm) => itm.template_id === templateId);
  const checkNarativeTemplate = () => { if (templateData && templateIndex !== -1) { return templateData[templateIndex]?.is_narrative_template === 1 ? true : false; } }
  const baseUrl = origin();
  let sessionAPIKey = sessionStorage.getItem("CK");
  let sessionSecretKey = sessionStorage.getItem("SK");

  const handleOpenDialog = async (event, dropdownId, dropdownsList) => {
    setAnchorEl(null);
    event?.stopPropagation();
    if ((event?.target?.name === "commentText" || event?.target?.name === "non-narrative-comment") && setActiveEditorId) {
      setActiveEditorId(event?.target?.name);
    }
    else if (event?.target?.name === "summaryText" && setActiveEditorId) {
      setActiveEditorId(event?.target?.name);
    }
    if (event?.target?.tagName === "BUTTON") {
      const parentSpan = event?.target?.closest('span[contenteditable="false"]');
      const parentSpanId = parentSpan?.id;
      let newButtonId = event?.target?.getAttribute("data-dropdown-id") ||
        event?.target?.id?.replace("dropdownButton", "").replace("dropdownIcon_", "");
      if (!newButtonId && parentSpanId) {
        newButtonId = parentSpanId.replace("[dropdown ", "").replace("]", "");
      }
      let dropdown;
      dropdown = dropdownList?.find((obj) => obj?.dropdown_uuid === newButtonId);
      if (dropdown === undefined) dropdown = editorData?.dropdownList?.find((obj) => obj?.dropdown_uuid === newButtonId);
      if (dropdown === undefined && dropdownsList) dropdown = dropdownsList?.find((obj) => obj?.dropdown_uuid === newButtonId);
      if (dropdown === undefined) {
        if (id === "commentText" || id === "non-narrative-comment") {
          dropdown = commentData?.comment_dropdowns?.find((obj) => obj?.dropdown_uuid === newButtonId);
        } else if (id === "summaryText") {
          dropdown = commentData?.summary_comment_dropdowns?.find((obj) => obj?.dropdown_uuid === newButtonId);
        } else if (id === "intro-text-editor") {
          const introDropdowns = normalizeDropdowns(commentData?.introductory_text_dropdowns);
          dropdown = introDropdowns?.find((obj) => obj?.dropdown_uuid === newButtonId);
        }
      }
      let response;
      const getFallbackOptions = () =>
        dropdown?.dropdowns_values?.length > 0
          ? getFilteredDropdownValues(dropdown)
          : dialogOptionList;
      let newOptions = [];
      if (dropdown?.list_guid && dropdown?.list_guid !== "undefined") {
        try {
          response = await axios.get(`${baseUrl}get-lists?guid=${dropdown?.list_guid}`, {
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${Base64.decode(sessionSecretKey)}`,
              "api-key": `${Base64.decode(sessionAPIKey)}`,
              platform: "web",
              "api-platform": "WRW",
            }
          });
          newOptions = response?.data?.data?.options || getFallbackOptions();
          if (response?.data?.data?.deleted_at) {
            let newDropdownList = editorData?.dropdownList || dropdownsList || ((id === "commentText" || id === "non-narrative-comment") ? commentData?.comment_dropdowns : commentData?.summary_comment_dropdowns);
            let newIndex = newDropdownList?.findIndex((obj => obj?.list_guid === dropdown?.list_guid))
            newDropdownList[newIndex] = { ...newDropdownList[newIndex], list_guid: undefined, dropdowns_values: response?.data?.data?.options };
            if (id === "intro-text-editor" && setEditorContent) {
              handleDropdownList?.(id, newDropdownList);
              setEditorContent(prevState => ({ ...prevState, dropdownList: newDropdownList }));
            } else if (id === "commentText" && setCommentText) {
              setCommentText(prevState => ({ ...prevState, dropdownList: newDropdownList, comment_dropdowns: newDropdownList }))
            } else if (id === "summaryText" && setCommentSummary) {
              setCommentSummary(prevState => ({ ...prevState, dropdownList: newDropdownList, summary_comment_dropdowns: newDropdownList }))
            } else if (id === "non-narrative-comment") {
              setEditorContent(prevState => ({ ...prevState, dropdownList: newDropdownList }));
            }
          }
        } catch (e) {
          console.log(e);
          newOptions = getFallbackOptions();
        }
      } else {
        newOptions = getFallbackOptions();
      }

      setIsDropdownLoading(false);
      setDropdownOptions(newOptions);
      const optionsList = response?.data?.data?.options || dropdown?.dropdowns_values || [];
      setState((prevState) => ({
        ...prevState,
        dialogOptionList: Array.isArray(optionsList) ? optionsList : [],
        open: "edit_list",
        selectedDropdown: newButtonId || dropdown?.dropdown_uuid,
        name: response?.data?.data?.name || dropdown?.name || "",
        description: response?.data?.data?.description || dropdown?.description || "",
        tags: response?.data?.data?.tags || dropdown?.tags || [],
        saveToGlobalLists: dropdown?.list_guid ? response?.data?.data?.deleted_at ? false : true : false,
        listId: response?.data?.data?.deleted_at ? undefined : response?.data?.data?.id || dropdown?.listId,
        listGuid: response?.data?.data?.deleted_at ? undefined : response?.data?.data?.list_guid || dropdown?.list_guid
      }));
    }
  };

  useEffect(() => {
    const editorInstance = editor?.current?.editor;
    let editorContent = editorInstance?.html?.get();
    if (!editorInstance || !editorData?.innerHTML) return;
    let isEffectExecuted = false;
    let newDropdownList = dropdownList?.length ? [...dropdownList] : [];
    let modifiedHtml;
    const replacePlaceholdersWithDropdowns = () => {
      const dropdowns = id === "intro-text-editor" ? normalizeDropdowns(commentData?.introductory_text_dropdowns) : editorData?.dropdownList;
      const regexPlainDropdown = /\[dropdown ([a-f0-9\-]{36})\]/gi;
      if (Array?.isArray(dropdowns) && dropdowns?.length > 0) {
        var placeholderRegex = /\[dropdown\s([a-f0-9-]+)\]/gi;
        var selectRegex = /<select\b[^>]*id="([a-f0-9-]+)"[^>]*>([\s\S]*?)<\/select>/gi;
        editorData.innerHTML = editorData.innerHTML
          ?.replace(placeholderRegex, (match, dropdownId) => createDropdownHTML(dropdownId))
          ?.replace(selectRegex, (match, dropdownId) => createDropdownHTML(dropdownId));
        function createDropdownHTML(dropdownId) {
          const dropdown = dropdowns?.find((item) => item?.dropdown_uuid === dropdownId);
          if (!dropdown) return "";
          const existingIndex = newDropdownList.findIndex(d => d.dropdown_uuid === dropdownId);
          if (existingIndex === -1) {
            newDropdownList.push({
              dropdown_uuid: dropdownId,
              name: dropdown?.name || "",
              selected_value: dropdown?.selected_value,
              dropdowns_values: dropdown?.dropdowns_values || [],
              list_guid: dropdown?.list_guid
            });
          }
          let selectedValue = dropdown?.selected_value
            ? /^[xX]+$/.test(dropdown.selected_value)
              ? ""
              : dropdown.selected_value
            : "";
          const safeValue = selectedValue.replace(/\\/g, "\\\\").replace(/"/g, "&quot;");
          const commentAttrs =
            id !== "intro-text-editor"
              ? ` comment_id="${commentData?.comment_id}" comment_uuid="${commentData?.comment_uuid}" dropType="${id === "commentText" ? "comment" : "summary"
              }"`
              : "";
          return `<span contenteditable="false" 
                        style="display:inline-flex; align-items:center; vertical-align:middle; line-height:normal;" 
                        id="dropdown_${dropdownId}">
                    <input id="${dropdownId}" value="${safeValue}" ${commentAttrs} class="froala-dropdown-input"
                      placeholder="Type or select from dropdown"
                      autocomplete="off"
                      autocorrect="off"
                      spellcheck="false"
                      style="width: 220px; background-color: #f1f9fe; height: 32px; 
                            padding: 4px 8px; vertical-align: middle;
                            border: 1px solid #4282e2; border-right: none; 
                            border-radius: 4px 0 0 4px; 
                            margin-left: 2px;
                            font-size: 14px; font-weight: 600; color: #3B3B3B;
                            cursor: text; text-align: left;" />
                    <button
                      contenteditable="false"
                      tabindex="-1"
                      id="dropdownIcon_${dropdownId}"
                      name="${id}"
                      type="button"
                      data-dropdown-id="${dropdownId}"
                      style="background-color: #f1f9fe; width: 23px; height: 23px;
                            border: 1px solid #4282e2; border-left: 1px solid #4282e2;
                            font-size: 14px; color: #4282e2;
                             ${(!rwSettingAccess && dropdown?.list_guid) ? `border-radius: 0px 4px 4px 0px;` : ""}
                            display: inline-flex; align-items: center; justify-content: center;
                            cursor: pointer;">▾</button>
                    ${(rwSettingAccess || !dropdown?.list_guid) ? `<button id="dropdownButton${dropdownId}" name="${id}"
                      contenteditable="false"
                      tabindex="-1"
                       type="button"
                              data-dropdown-id="${dropdownId}"
                      style="background-color: #f1f9fe; width: 23px; height: 23px;
                            border: 1px solid #4282e2; border-left: none;
                            border-radius: 0 4px 4px 0;
                            font-size: 14px; color: #4282e2;
                            display: inline-flex; align-items: center; justify-content: center;
                            cursor: pointer;">✎</button>`: ""}
                  </span>`;
        }
      } else {
        const outputString = editorData?.innerHTML?.replace(regexPlainDropdown, (match, selectId) => {
          const dropdown = dropdowns?.find((item) => item?.dropdown_uuid === selectId);
          return `< span id = "[dropdown ${selectId}]" contenteditable = "false" style = "display: inline-flex; align-items: center;" ><input id="${selectId}" value=""
                              placeholder="Type or select from dropdown"
                              autocomplete="off"
                              autocorrect="off"
                              spellcheck="false"
                              style="width: 220px;
                              margin-left: 2px;
                              background-color: #f1f9fe;
                              height: 24px !important; 
                              padding: 4px 8px;
                              vertical-align: middle;
                              border-top: 1px solid #4282e2;
                              border-bottom: 1px solid #4282e2;
                              border-left: 1px solid #4282e2;
                              border-right: none; 
                              border-radius: 4px 0px 0px 4px; 
                              font-size: 14px;
                              font-weight: 600;
                              color: #3B3B3B;
                              cursor: text;
                              text-align: left;
                              display: inline-block;"
                              class="froala-dropdown-input"
                              />
                               <style>
                              .froala-dropdown-input::placeholder {
                                font-style: italic ! important;
                                font-weight:400 ! important;
                                }
                              </style><button type="button" contenteditable="false"
                                name="${id}"
                                id="dropdownIcon_${selectId}"
                                data-dropdown-id="${selectId}"
                                style="background-color: #f1f9fe;
                                  width: 26px;
                                  height: 24px !important;
                                  border: 1px solid #4282e2;
                                  border-right: 1px solid #4282e2;
                                  vertical-align: middle;
                                  margin-left: -3px;
                                  font-size: 14px;
                                  color: #4282e2;
                                  display: inline-flex;
                                  align-items: center;
                                  justify-content: center;
                                  cursor: pointer;
                                  padding-right: 2px;
                                  ${(!rwSettingAccess && dropdown?.list_guid) ? `border-radius: 0px 4px 4px 0px;` : ""}">
                                ▾
                              </button>${(rwSettingAccess || !dropdown?.list_guid) ? `<button contenteditable="false"
                            id="dropdownButton${selectId}"
                            name="${id}"
                            style="
                              background-color: #f1f9fe;
                              width: 24px;
                              height: 24px;
                              border: 1px solid #4282e2;
                              border-radius: 0 4px 4px 0;
                              margin-left: -3px;
                              font-size: 14px;
                              color: #4282e2;
                              display: inline-flex;
                              align-items: center;
                              justify-content: center;
                              cursor: pointer;
                            "
                          >✎</button>`: ""}</span > `;
        });
        editorData.innerHTML = outputString;
      }
      return editorData.innerHTML?.replace(regexPlainDropdown, "");
    };
    const replaceInputs = (input_text) => {
      let container = document.createElement("div");
      container.innerHTML = input_text;
      container.innerHTML = container.innerHTML.replace(/\[textbox(.*?)\]/g, (match, attributes) => {
        let idMatch = attributes.match(/id:([^;]+);?/);
        let valueMatch = attributes.match(/value:([^;]+);?/);
        let id = idMatch && idMatch[1].length > 30 ? idMatch[1].trim() : uuid();
        let value = valueMatch ? valueMatch[1].trim() : "";
        const safeValue = value.replace(/\\/g, "\\\\").replace(/"/g, '&quot;');
        return `<input id="${id}" value="${safeValue}" title="${safeValue}" style="width: 104px;
                background-color: #f1f9fe;
                height: 24px !important;
                overflow-wrap: break-word;
                padding: 1px 17px 3px 6px;
                border-radius: 4px;
                border: 1px solid #0F75BC;
                appearance: none;
                -webkit-appearance: none;
                -moz-appearance: none;
                font-size: 14px !important;
                color: #3B3B3B !important;
                font-style: normal !important;
                font-weight: 600 !important;
                max-width: 200px !important;
                min-width: 104px !important;
                margin-left: 2px;
                text-overflow: ellipsis !important;
                white-space: nowrap !important;"
                />`;
      });
      return container.innerHTML;
    }
    const isIntroTextEmpty =
      commentData?.introductory_text === "<p></p>" || commentData?.introductory_text === "";
    const isEditorEmpty = !editorData?.innerHTML || editorData?.innerHTML === "<p></p>";
    if (initTimeoutRef.current) {
      clearTimeout(initTimeoutRef.current);
      initTimeoutRef.current = null;
    }
    initTimeoutRef.current = setTimeout(async () => {
      if ((id !== "intro-text-editor") && type !== "addComment") {
        if (isIntroTextEmpty && isEditorEmpty) {
          editorInstance?.html?.set("<p></p>");
        }
        else if (editorData?.innerHTML || editorContent) {
          if (!isEffectExecuted || ((!editorContent || editorContent === undefined) && editorInstance)) {
            isEffectExecuted = true;
            let newHtmlData = replacePlaceholdersWithDropdowns();
            if (modifiedHtml) modifiedHtml = replaceInputs(modifiedHtml);
            else if (newHtmlData) newHtmlData = replaceInputs(newHtmlData);
            const currentHtml = editorInstance.html.get();
            const shouldSetHtml = !isFocused && (!currentHtml || currentHtml === "<p></p>" || currentHtml === "<p><br></p>" || !editorContent);
            if (shouldSetHtml) {
              let selection = null;
              let range = null;
              try {
                selection = editorInstance.selection.get();
                if (selection && selection.rangeCount > 0) {
                  range = editorInstance.selection.ranges(0);
                }
              } catch (e) {
              }
              editorInstance.html.set(modifiedHtml || newHtmlData || editorData?.innerHTML || "");
              if (range) {
                setTimeout(() => {
                  try {
                    editorInstance.selection.setRange(range);
                  } catch (e) {
                    try {
                      editorInstance.selection.clear();
                      editorInstance.selection.setAtEnd(editorInstance.$el.get(0));
                    } catch (e2) {
                      editorInstance.events.focus(true);
                    }
                  }
                }, 10);
              }
              if (setEditorContent) setEditorContent(prevState => ({ ...prevState, innerHTML: modifiedHtml || newHtmlData || editorData?.innerHTML || "" }))
            }
          }
        }
        else {
          editorInstance?.html?.set("<p></p>");
        }
      }
    }, 100);
    return () => {
      if (initTimeoutRef.current) {
        clearTimeout(initTimeoutRef.current);
        initTimeoutRef.current = null;
      }
      isEffectExecuted = false;
    };
  }, [commentData, id, rwSettingAccess, listGuid]);

  const handleModelChange = (model) => {
    if (suppressModelChangeRef.current) return;
    if (setCopied) setCopied(false)
    let htmlString = model;
    const parser = new DOMParser();
    const doc = parser.parseFromString(htmlString, "text/html");
    const pastedElements = doc?.querySelectorAll(`[id ^= "isPasted"], [id ^= "pasted/"], #isPasted`);
    const spanElements = doc?.querySelectorAll("span:not([id]):has(select)");
    let checkPaste = false;
    if (pastedElements.length > 0) {
      pastedElements.forEach((element) => {
        if (element?.tagName === "SPAN" || element?.tagName === "SELECT") {
          if (element?.tagName === "SPAN") {
            const selectElement = element?.querySelector("select");
            if (selectElement) {
              checkPaste = true;
              element.remove();
            }
          } else if (element?.tagName === "SELECT") {
            checkPaste = true;
            element.remove();
          }
        } else if (element?.tagName === "BR" || element?.tagName === "P") {
          function findNextSpan(node) {
            while (node) {
              if (node?.tagName === "SPAN" || node?.tagName === "SELECT") return node;
              if (node?.tagName === "P") {
                const nestedSpan = node?.querySelector("span");
                if (nestedSpan) return nestedSpan;
                const nestedSelect = node?.querySelector("select");
                if (nestedSelect) return nestedSelect;
              }
              node = node.nextElementSibling;
            }
            return null;
          }
          const targetSpan = findNextSpan(element.nextElementSibling);
          if (targetSpan?.tagName === "SPAN") {
            const selectElement = targetSpan?.querySelector("select");
            if (selectElement) {
              checkPaste = true;
              selectElement.remove();
            }
          } else if (targetSpan?.tagName === "SELECT") {
            checkPaste = true;
            targetSpan.remove();
          }
          else if (element?.tagName === "INPUT") {
            checkPaste = false;
            const newInputUuid = uuid();
            element.id = newInputUuid;
          }
        } else if (element?.tagName === "INPUT") {
          checkPaste = false;
          const newInputUuid = uuid();
          element.id = newInputUuid;
        }
      });
      if (spanElements.length > 0) {
        spanElements.forEach((element) => {
          const selectElement = element?.querySelector("select");
          if (selectElement && !selectElement?.id) {
            checkPaste = true;
            selectElement.remove();
          }
          const buttonElement = element?.querySelector("button");
          if (buttonElement && !buttonElement?.id) {
            checkPaste = true;
            buttonElement.remove();
          }
        });
      }
    }
    const inputs = doc.body.querySelectorAll("input");
    inputs.forEach((element) => {
      if (!element.id || element.id === "isPasted" || document.getElementById(element.id)) {
        let newInputUuid;
        do {
          newInputUuid = uuid();
        } while (document.getElementById(newInputUuid));
        element.id = newInputUuid;
      }
    });
    if (checkPaste) {
      setState((prevState) => ({ ...prevState, openRemoveDropdown: doc.body.innerHTML }));
    } else {
      setTimeout(() => {
        if (suppressModelChangeRef.current) return;
        syncDynamicInputValuesToAttributes(editor.current?.editor?.$el?.get(0));
        const updatedContent = editor.current?.editor?.html.get();
        onChange(id, updatedContent);
      }, 0);
    }
    if (setIsFocused && id !== "intro-text-editor") setIsFocused(true);
  };

  const cleanPastedHtml = (htmlString) => {
    const parser = new DOMParser();
    const doc = parser.parseFromString(htmlString, "text/html");
    doc.querySelectorAll('span[id^="dropdown_"]').forEach(span => span.remove());
    return doc.body.textContent.trim();
  };

  const handlePasteBefore = (event) => {
    const editorInstance = editor?.current?.editor;
    if (!editorInstance || !event.clipboardData) return;
    const pasteHtml = event.clipboardData.getData("text/html") || event.clipboardData.getData("text/plain");
    const parser = new DOMParser();
    const doc = parser.parseFromString(pasteHtml, "text/html");
    const selects = doc.querySelectorAll("input.froala-dropdown-input");
    const links = doc.querySelectorAll("a");
    const hasURL = /(https?:\/\/[^\s<]+|www\.[^\s<]+[^.,;:"')\]\s<])/gi.test(pasteHtml);
    const isWordContent = /class="?Mso|style="[^"]*mso-|<o:p>|<!--\[if gte mso|xmlns:o="/i.test(pasteHtml);
    const removeWordArtifacts = (node) => {
      const walker = document.createTreeWalker(node, NodeFilter.SHOW_ALL, null, false);
      let current;
      while ((current = walker.nextNode())) {
        if (current.nodeType === Node.COMMENT_NODE) {
          current.remove(); // Remove <!--[if gte mso]> ... <![endif]-->
        } else if (current.nodeType === Node.ELEMENT_NODE) {
          const tag = current.nodeName.toLowerCase();
          // Remove <o:p> and other office-specific tags
          if (tag.startsWith("o:") || tag.startsWith("v:") || tag === "o:p") {
            current.remove();
            continue;
          }
          // Strip mso styles
          if (current.hasAttribute("style")) {
            const cleanedStyle = current
              .getAttribute("style")
              .replace(/mso-[^:;]+:[^;]+;?/gi, "")
              .replace(/background(-color)?:[^;]+;?/gi, "");
            current.setAttribute("style", cleanedStyle);
          }
          // Strip Word classes
          if (current.hasAttribute("class") && /Mso|WordSection/i.test(current.getAttribute("class"))) {
            current.removeAttribute("class");
          }
        }
      }
    };
    removeWordArtifacts(doc.body);
    const cleanedHTML = doc.body.innerHTML.trim();
    if (isWordContent || selects.length > 0 || hasURL) {
      event.preventDefault();
    }
    if (selects.length > 0 && [...selects].every((item) => item.id?.trim() !== "")) {
      event.preventDefault();
      setState((prev) => ({
        ...prev,
        openRemoveDropdown: cleanPastedHtml(cleanedHTML),
      }));
      return false;
    }
    if (isWordContent) {
      setState((prev) => ({
        ...prev,
        openWordPastePopup: {
          raw: pasteHtml,
          clean: cleanPastedHtml(cleanedHTML),
        },
      }));
      return;
    }
    if (hasURL && !links.length) {
      let formattedHtml = cleanedHTML.replace(
        /((https?:\/\/|www\.)[^\s<]+[^.,;:"')\]\s<])/gi,
        '<a href="$1" target="_blank">$1</a>'
      );
      formattedHtml = formattedHtml.replace(/href="www\./g, 'href="http://www.');
      editorInstance?.html?.insert(formattedHtml);
      return;
    }
  };

  const handleInputBlur = (event, inputId, value, dropdownsList) => {
    if (event?.target?.tagName === "SELECT") return;
    // Skip persisting the typed value when the blur is caused by interacting with the
    // dropdown popover (open) or while a selection is being applied. Otherwise the
    // previously typed text would overwrite the value chosen from the dropdown.
    if (anchorElRef.current || suppressBlurPersistRef.current) return;
    const editorInstance = editor.current?.editor;
    if (!editorInstance) return;
    const editorElement = editorInstance.$el.get(0);
    let domMutated = false;
    if (relocateDynamicInputsIntoEditorRoot(editorElement)) domMutated = true;
    if (removeEmptyBlocksAfterDynamicInputs(editorElement)) domMutated = true;
    if (domMutated) {
      editorInstance.events.trigger("contentChanged");
    }

    let newDropdownList = dropdownsList ? [...dropdownsList] : editorData?.dropdownList || [];
    const allInputs = editorElement.querySelectorAll('input');
    allInputs.forEach(input => {
      const newValue = input.id === inputId ? input.value.replace(/"/g, "&quot;").trim() : input.value.trim();
      input.setAttribute("value", newValue);
      input.setAttribute("title", newValue);
      if (input.classList.contains('froala-dropdown-input')) {
        let dropdownIndex = newDropdownList.findIndex(d => d.dropdown_uuid === input.id);
        if (dropdownIndex !== -1) {
          newDropdownList[dropdownIndex] = {
            ...newDropdownList[dropdownIndex],
            selected_value: newValue
          };
        } else {
          newDropdownList.push({
            dropdown_uuid: input.id,
            name: "",
            selected_value: newValue,
            dropdowns_values: dialogOptionList
          });
        }
      }
    });
    editorInstance.events.trigger("contentChanged");
    const updatedHtml = editorInstance.html.get();
    if (id === "intro-text-editor" && setEditorContent) {
      setEditorContent(prevState => ({ ...prevState, innerHTML: updatedHtml, dropdownList: newDropdownList }));
    } else if (id === "commentText" && setCommentText) {
      setCommentText(prev => ({ ...prev, innerHTML: updatedHtml, dropdownList: newDropdownList, comment_dropdowns: newDropdownList }));
    } else if (id === "summaryText" && setCommentSummary) {
      setCommentSummary(prev => ({ ...prev, innerHTML: updatedHtml, dropdownList: newDropdownList, summary_comment_dropdowns: newDropdownList }));
    } else if (id === "non-narrative-comment" && setEditorContent) {
      setEditorContent(prevState => ({ ...prevState, innerHTML: updatedHtml, dropdownList: newDropdownList }));
    }
    if (id !== "intro-text-editor") {
      handleDropdownList?.(id, newDropdownList, "blur", inputId);
    }
    const hasDropdownInputs = Array.from(allInputs).some(input => input.classList.contains('froala-dropdown-input'));
    if (handleIntroSave) {
      if (hasDropdownInputs) {
        const changedDropdown = newDropdownList.find(d => d.dropdown_uuid === inputId);
        handleIntroSave(changedDropdown ? [changedDropdown] : undefined, null, undefined, false);
      } else if (hasChanges) {
        handleIntroSave(null, null, updatedHtml);
      }
    }
    if (setHasChanges) setHasChanges(false);
  };

  useEffect(() => {
    const editorInstance = editor?.current?.editor;
    if (!editorInstance) return;
    const contentArea = editorInstance.$el.get(0);
    if (!contentArea) return;
    const getDropdownListForLookup = () =>
      mergeDropdownListsForLookup(editorDataRef.current?.dropdownList, stateRef.current?.dropdownList);

    const handleButtonClick = async (event) => {
      const button = event.target.closest('button[id^="dropdownButton"], button[id^="dropdownIcon_"]');
      if (!button) return;
      event.stopPropagation();
      event.preventDefault();
      window.dispatchEvent(new CustomEvent('froala-dropdown-open', {
        detail: { instanceId: instanceId.current }
      }));
      setIsDropdownLoading(true);
      setDropdownOptions([]);
      setActiveEditorId?.(button.name || event.target.name);
      const buttonText = button.textContent.trim();
      setAnchorEl(button);
      const inputId = button.id.replace("dropdownButton", "").replace("dropdownIcon_", "");
      setCurrentInputId(inputId);
      if (buttonText === "▾") {
        const requestId = ++dropdownOpenRequestRef.current;
        let dropDown;
        const dropdownListForLookup = getDropdownListForLookup();
        if (id === "intro-text-editor" || type === "edit") {
          const introDropdowns = normalizeDropdowns(commentData?.introductory_text_dropdowns);
          dropDown = dropdownListForLookup.find((e) => e?.dropdown_uuid === inputId) || introDropdowns?.find((e) => e?.dropdown_uuid === inputId);
        } else {
          const persistedDropdowns =
            id === "commentText" || id === "non-narrative-comment"
              ? commentData?.comment_dropdowns
              : id === "summaryText"
                ? commentData?.summary_comment_dropdowns
                : null;
          dropDown =
            dropdownListForLookup.find((e) => e?.dropdown_uuid === inputId) ||
            persistedDropdowns?.find((e) => e?.dropdown_uuid === inputId);
        }
        if (dropDown === undefined) {
          const introDropdowns = normalizeDropdowns(commentData?.introductory_text_dropdowns);
          dropDown = dropdownListForLookup.find((e) => e?.dropdown_uuid === inputId) || introDropdowns?.find((e) => e?.dropdown_uuid === inputId);
        }
        let newOptions = [];
        let response;
        if (dropDown?.list_guid && dropDown?.list_guid !== "undefined") {
          try {
            response = await axios.get(`${baseUrl}get-lists?guid=${dropDown?.list_guid}`, {
              headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${Base64.decode(sessionSecretKey)} `,
                "api-key": `${Base64.decode(sessionAPIKey)} `,
                platform: "web",
                "api-platform": "WRW",
              }
            });
            if (requestId !== dropdownOpenRequestRef.current) return;
            newOptions = normalizeApiDropdownOptions(response?.data?.data?.options);
            if (response?.data?.data?.deleted_at) {
              const introDropdowns = normalizeDropdowns(commentData?.introductory_text_dropdowns);
              const persistedFallback =
                id === "intro-text-editor" || type === "edit"
                  ? introDropdowns
                  : (id === "commentText" || id === "non-narrative-comment")
                    ? commentData?.comment_dropdowns
                    : commentData?.summary_comment_dropdowns;
              const sourceCandidates = [
                dropdownListForLookup,
                Array.isArray(persistedFallback) ? persistedFallback : [],
                introDropdowns,
              ];
              let sourceList = [];
              let newIndex = -1;
              for (const candidate of sourceCandidates) {
                if (!Array.isArray(candidate) || !candidate.length) continue;
                const index = candidate.findIndex(
                  (obj) => obj?.list_guid === dropDown?.list_guid || obj?.dropdown_uuid === inputId
                );
                if (index !== -1) {
                  sourceList = candidate;
                  newIndex = index;
                  break;
                }
              }
              const apiOptions = normalizeApiDropdownOptions(response?.data?.data?.options);
              if (newIndex !== -1) {
                const newDropdownList = sourceList.map((item, index) =>
                  index === newIndex
                    ? { ...item, list_guid: undefined, dropdowns_values: apiOptions }
                    : item
                );
                const dropdownSpan = button.closest('span[id^="dropdown_"]');
                if (dropdownSpan && !dropdownSpan.querySelector(`button[id^="dropdownButton"]`)) {
                  const editButton = document.createElement('button');
                  editButton.id = `dropdownButton${inputId}`;
                  editButton.name = id;
                  editButton.contentEditable = "false";
                  editButton.tabIndex = "-1";
                  editButton.type = "button";
                  editButton.setAttribute("data-dropdown-id", inputId);
                  editButton.style.cssText = "background-color: #f1f9fe; width: 23px; height: 23px; border: 1px solid #4282e2; border-left: none; border-radius: 0 4px 4px 0; font-size: 14px; color: #4282e2; display: inline-flex; align-items: center; justify-content: center; cursor: pointer;";
                  editButton.textContent = "✎";
                  button.style.borderRadius = "0px";
                  button.after(editButton);
                  editButton.addEventListener('click', handleButtonClick);
                  const editorInstance = editor?.current?.editor;
                  if (editorInstance) {
                    const updatedHtml = editorInstance.html.get();
                    if (id === "intro-text-editor" && setEditorContent) {
                      setEditorContent(prevState => ({ ...prevState, innerHTML: updatedHtml, dropdownList: newDropdownList }));
                    } else if (id === "commentText" && setCommentText) {
                      setCommentText(prevState => ({ ...prevState, innerHTML: updatedHtml, dropdownList: newDropdownList }));
                    } else if (id === "summaryText" && setCommentSummary) {
                      setCommentSummary(prevState => ({ ...prevState, innerHTML: updatedHtml, dropdownList: newDropdownList }));
                    } else if (id === "non-narrative-comment" && setEditorContent) {
                      setEditorContent(prevState => ({ ...prevState, innerHTML: updatedHtml, dropdownList: newDropdownList }));
                    }
                  }
                }
                if (id === "intro-text-editor" && setEditorContent) {
                  handleDropdownList?.(id, newDropdownList);
                  setEditorContent(prevState => ({ ...prevState, dropdownList: newDropdownList }));
                } else if (id === "commentText" && setCommentText) {
                  setCommentText(prevState => ({ ...prevState, dropdownList: newDropdownList, comment_dropdowns: newDropdownList }))
                } else if (id === "summaryText" && setCommentSummary) {
                  setCommentSummary(prevState => ({ ...prevState, dropdownList: newDropdownList, summary_comment_dropdowns: newDropdownList }))
                } else if (id === "non-narrative-comment") {
                  setEditorContent(prevState => ({ ...prevState, dropdownList: newDropdownList }));
                }
                setState((prevState) => ({ ...prevState, listGuid: undefined, listId: undefined }));
              }
            }
          } catch (e) {
            if (requestId !== dropdownOpenRequestRef.current) return;
            console.log(e);
            newOptions = getFilteredDropdownValues(dropDown);
          }
        } else {
          newOptions = (() => {
            return dropDown?.dropdowns_values?.length > 0
              ? getFilteredDropdownValues(dropDown)
              : (stateRef.current?.dialogOptionList || []);
          })();
        }
        if (requestId !== dropdownOpenRequestRef.current) return;
        setIsDropdownLoading(false);
        setDropdownOptions(newOptions);
        if (document.body.contains(button)) {
          setAnchorEl(button);
        }
      }
      else if (buttonText === "✎") {
        dropdownOpenRequestRef.current += 1;
        setAnchorEl(null);
        setCurrentInputId(null);
        setDropdownOptions([]);
        setIsDropdownLoading(false);
        event.stopPropagation();
        handleOpenDialog(event, null, id === "intro-text-editor" ? normalizeDropdowns(commentData?.introductory_text_dropdowns) :
          id === "commentText" ? commentData?.comment_dropdowns :
            editorData?.dropdownList);
      }
    };
    const attachDropdownButtonListeners = (button) => {
      button.removeEventListener('click', handleButtonClick);
      button.addEventListener('click', handleButtonClick);
    };
    const handleInputBlurWrapper = (event, dropdownList) => {
      if (event.target.tagName === "INPUT") {
        handleInputBlur(event, event.target.id, event.target.value, dropdownList);
      }
    };
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (node.nodeType === Node.ELEMENT_NODE) {
            if (node.matches?.('button[id^="dropdownButton"], button[id^="dropdownIcon_"]')) {
              attachDropdownButtonListeners(node);
            }
            const buttons = node.querySelectorAll('button[id^="dropdownButton"], button[id^="dropdownIcon_"]');
            buttons.forEach(attachDropdownButtonListeners);
            const inputs = node.querySelectorAll('input');
            inputs.forEach(input => {
              input.removeEventListener('blur', handleInputBlurWrapper);
              input.addEventListener('blur', (event) => handleInputBlurWrapper(event, editorDataRef.current?.dropdownList));
            });
          }
        });
      });
    });
    const buttons = contentArea.querySelectorAll('button[id^="dropdownButton"], button[id^="dropdownIcon_"]');
    buttons.forEach(attachDropdownButtonListeners);
    const inputs = contentArea.querySelectorAll('input');
    inputs.forEach(input => {
      input.removeEventListener('blur', handleInputBlurWrapper);
      input.addEventListener('blur', handleInputBlurWrapper);
    });
    observer.observe(contentArea, { childList: true, subtree: true });
    return () => {
      buttons.forEach(button => {
        button.removeEventListener('click', handleButtonClick);
      });
      inputs.forEach(input => {
        input.removeEventListener('blur', handleInputBlurWrapper);
      });
      observer.disconnect();
    };
  }, [editorData, commentData, commentData?.summary_comment_dropdowns, commentData?.introductory_text_dropdowns]);

  useEffect(() => {
    const handleScroll = () => {
      setAnchorEl(null);
    };
    let froalaScrollContainer = null;
    let retryCount = 0;
    const maxRetries = 10;
    const retryDelay = 200;
    let retryTimer;
    const tryAttachFroalaScroll = () => {
      froalaScrollContainer =
        document.querySelector('.fr-scroller') ||
        document.querySelector('.fr-wrapper') ||
        document.querySelector('.fr-element') ||
        document.querySelector('.fr-box.fr-basic');
      if (froalaScrollContainer) {
        froalaScrollContainer.addEventListener('scroll', handleScroll);
      } else if (retryCount < maxRetries) {
        retryCount++;
        retryTimer = setTimeout(tryAttachFroalaScroll, retryDelay);
      } else {
        console.warn("Froala scroll container not found after retries.");
      }
    };
    tryAttachFroalaScroll();
    return () => {
      if (froalaScrollContainer) {
        froalaScrollContainer.removeEventListener('scroll', handleScroll);
      }
      clearTimeout(retryTimer);
    };
  }, []);

  const handleDeleteDropdown = useCallback((dropdownId) => {
    if (!dropdownId) return;
    const editorInstance = editor?.current?.editor;
    if (!editorInstance) return;
    editorInstance.undo.saveStep();
    const wrapper = document.createElement("div");
    wrapper.innerHTML = editorInstance.html.get();
    const dropdownEl = wrapper.querySelector(`span[id = "dropdown_${dropdownId}"]`);
    if (dropdownEl) {
      dropdownEl.remove();
      wrapper.innerHTML = wrapper.innerHTML.replace(/(&nbsp;|\u00A0)+/g, " ");
      const cleaned = wrapper.innerHTML;
      editorInstance.html.set(cleaned);
      editorInstance.events.focus(true);
      editorInstance.undo.saveStep();
      let currentDropdownList =
        id === "intro-text-editor"
          ? normalizeDropdowns(commentData?.introductory_text_dropdowns)
          : editorData?.dropdownList;
      const updatedDropdownList = currentDropdownList?.filter((item) => item?.dropdown_uuid !== dropdownId);
      if (id === "intro-text-editor") {
        setEditorContent?.(prevState => ({ ...prevState, innerHTML: cleaned }));
      } else if (id === "commentText" && setCommentText) {
        setCommentText(prevState => ({ ...prevState, innerHTML: cleaned, dropdownList: updatedDropdownList }));
      } else if (id === "summaryText" && setCommentSummary) {
        setCommentSummary(prevState => ({ ...prevState, innerHTML: cleaned, dropdownList: updatedDropdownList }));
      } else if (id === "non-narrative-comment") {
        setEditorContent(prevState => ({ ...prevState, innerHTML: cleaned, dropdownList: updatedDropdownList }));
      }
      handleDropdownList?.(id, updatedDropdownList);
    }
  }, [editor, id, commentData, setEditorContent, setCommentText, setCommentSummary, handleDropdownList]);

  useEffect(() => {
    const editorInstance = editor?.current?.editor;
    if (!editorInstance) return;
    const handleKeyDown = (event) => {
      if (event.key === 'Delete' || event.key === 'Backspace') {
        const selection = editorInstance.selection.get();
        if (!selection) return;
        const selectedNode = selection.startContainer || selection.commonAncestorContainer;
        const dropdownElement = editorInstance.$(selectedNode).closest('span[id^="dropdown_"]')[0];
        setTimeout(() => {
          const currentContent = editorInstance.html.get();
          const isEmpty = !currentContent ||
            currentContent === "" ||
            currentContent === "<p><br></p>" ||
            currentContent === "<p></p>" ||
            currentContent === "<br>";
          if (isEmpty && !dropdownElement) {
            if (id === "intro-text-editor")
              setEditorContent?.(prevState => ({ ...prevState, innerHTML: "", dropdownList: [] }));
            else if (id === "commentText" && setCommentText)
              setCommentText(prevState => ({ ...prevState, innerHTML: "", dropdownList: [] }));
            else if (id === "summaryText" && setCommentSummary)
              setCommentSummary(prevState => ({ ...prevState, innerHTML: "", dropdownList: [] }));
            else if (id === "non-narrative-comment") {
              setEditorContent(prevState => ({ ...prevState, innerHTML: "", dropdownList: [] }));
            }
            if (setHasChanges) setHasChanges(true);
          }
        }, 0);
      }  else if (
        /^[a-z0-9]$/i.test(event.key) &&
        !event.ctrlKey &&
        !event.metaKey &&
        !event.altKey
      ) {
        setTimeout(() => {
          const currentContent = editorInstance.html.get();
          handleModelChange(currentContent);
        }, 0);
      }
    };
    const editorElement = editorInstance.$el.get(0);
    editorElement.addEventListener('keydown', handleKeyDown);
    return () => {
      editorElement.removeEventListener('keydown', handleKeyDown);
    };
  }, [editor, handleDeleteDropdown]);

  useEffect(() => {
    const handleDropdownDeleteClick = (event) => {
      if (event.target.id.startsWith("dropdownDelete")) {
        const dropdownId = event.target.id.replace("dropdownDelete", "");
        handleDeleteDropdown(dropdownId);
      }
    };
    document.addEventListener("click", handleDropdownDeleteClick);
    return () => {
      document.removeEventListener("click", handleDropdownDeleteClick);
    };
  }, []);

  useEffect(() => {
    const getDropdownListForLookup = () =>
      mergeDropdownListsForLookup(editorDataRef.current?.dropdownList, stateRef.current?.dropdownList);

    const resolveDropDownForInput = (inputId) => {
      const dropdownListForLookup = getDropdownListForLookup();
      let dropDown;
      if (id === "intro-text-editor" || type === "edit") {
        const introDropdowns = normalizeDropdowns(commentData?.introductory_text_dropdowns);
        dropDown = dropdownListForLookup.find((e) => e?.dropdown_uuid === inputId) || introDropdowns?.find((e) => e?.dropdown_uuid === inputId);
      } else {
        const persistedDropdowns =
          id === "commentText" || id === "non-narrative-comment"
            ? commentData?.comment_dropdowns
            : id === "summaryText"
              ? commentData?.summary_comment_dropdowns
              : null;
        dropDown =
          dropdownListForLookup.find((e) => e?.dropdown_uuid === inputId) ||
          persistedDropdowns?.find((e) => e?.dropdown_uuid === inputId);
      }
      if (dropDown === undefined) {
        const introDropdowns = normalizeDropdowns(commentData?.introductory_text_dropdowns);
        dropDown = dropdownListForLookup.find((e) => e?.dropdown_uuid === inputId) || introDropdowns?.find((e) => e?.dropdown_uuid === inputId);
      }
      return dropDown;
    };

    const openDropdownForInstance = async (inputId, event) => {
      event.stopPropagation();
      event.preventDefault();
      window.dispatchEvent(new CustomEvent('froala-dropdown-open', {
        detail: { instanceId: instanceId.current }
      }));
      const input = document.getElementById(inputId);
      if (!input) return;
      const editorElement = document.getElementById(id);
      if (!editorElement?.contains(input)) return;
      const iconButton = document.getElementById(`dropdownIcon_${inputId}`);
      const anchor = iconButton || input;
      setIsDropdownLoading(true);
      setDropdownOptions([]);
      setCurrentInputId(inputId);
      setAnchorEl(anchor);
      const requestId = ++dropdownOpenRequestRef.current;

      const dropDown = resolveDropDownForInput(inputId);
      let newOptions = [];
      if (dropDown?.list_guid && dropDown?.list_guid !== "undefined") {
        try {
          const response = await axios.get(`${baseUrl}get-lists?guid=${dropDown?.list_guid}`, {
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${Base64.decode(sessionSecretKey)} `,
              "api-key": `${Base64.decode(sessionAPIKey)} `,
              platform: "web",
              "api-platform": "WRW",
            }
          });
          if (requestId !== dropdownOpenRequestRef.current) return;
          newOptions = normalizeApiDropdownOptions(response?.data?.data?.options);
        } catch (e) {
          if (requestId !== dropdownOpenRequestRef.current) return;
          console.log(e);
          newOptions = getFilteredDropdownValues(dropDown);
        }
      } else {
        const normalizeValues = (v) => Array.isArray(v?.values) ? v.values : (Array.isArray(v) ? v : []);
        const filterValues = (arr) => normalizeValues(arr).filter(v => !v?.toUpperCase?.().includes("XXX"));
        const dropType = input.getAttribute('dropType') ||
          (id === 'summaryText' ? 'summary' :
            (id === 'commentText' || id === 'non-narrative-comment') ? 'comment' :
              (id === 'intro-text-editor' ? 'intro' : 'unknown'));
        let inputList = dropDown?.dropdowns_values;
        const normalizedList = normalizeValues(inputList);
        if (!normalizedList || normalizedList.length === 0) {
          if (dropType === 'comment') {
            inputList = commentData?.comment_dropdowns?.find((o) => o?.dropdown_uuid === inputId)?.dropdowns_values;
          } else if (dropType === 'summary') {
            inputList = commentData?.summary_comment_dropdowns?.find((o) => o?.dropdown_uuid === inputId)?.dropdowns_values;
          } else if (dropType === 'intro') {
            const rawIntroDropdowns = commentData?.introductory_text_dropdowns;
            const introDropdownsArray = Array.isArray(rawIntroDropdowns) ? rawIntroDropdowns
              : Array.isArray(rawIntroDropdowns?.introductory_text_dropdowns) ? rawIntroDropdowns.introductory_text_dropdowns
              : [];
            inputList = introDropdownsArray.find((o) => o?.dropdown_uuid === inputId)?.dropdowns_values;
          } else {
            inputList = stateRef.current?.dialogOptionList || [];
          }
        }
        newOptions = filterValues(inputList) || [];
      }
      if (requestId !== dropdownOpenRequestRef.current) return;
      setDropdownOptions(newOptions);
      setIsDropdownLoading(false);
      if (document.body.contains(anchor)) {
        setAnchorEl(anchor);
      }
    };
    const functionName = `openDropdown_${instanceId.current}`;
    window[functionName] = openDropdownForInstance;
    window.openDropdown = openDropdownForInstance;
    const bindDropdownEvents = () => {
      const dropdownInputs = document.querySelectorAll(".froala-dropdown-input");
      dropdownInputs.forEach((input) => {
        input.onclick = (event) => {
          event.preventDefault();
          setCurrentInputId(input.id);
          if (setActiveEditorId) setActiveEditorId(input.name)
        };
        input.onfocus = () => {
          lastFocusedDropdownRef.current = input.closest('span[id^="dropdown_"]');
        };
        input.onblur = (event) => {
          if (!event.relatedTarget?.id?.startsWith('add-input')) {
            lastFocusedDropdownRef.current = null;
          }
        };
        input.oninput = (event) => {
          const value = event.target.value;
          input.value = value;
          setState((prevState) => {
            const prevList = prevState.dropdownList?.length ? prevState.dropdownList : editorData?.dropdownList || [];
            const index = prevList.findIndex((obj) => obj?.dropdown_uuid === event.target.id);
            if (index === -1) return prevState;
            const oldValue = prevList[index]?.selected_value;
            if (oldValue === value) return prevState;
            const newList = [...prevList];
            newList[index] = {
              ...newList[index],
              selected_value: value || ""
            };
            return { ...prevState, dropdownList: newList, dialogOptionList: prevState.dialogOptionList };
          });
        };

      });
    };
    const cleanupRemovedInputs = () => {
      const dropdownInputs = document.querySelectorAll(".froala-dropdown-input");
      const activeIds = new Set(Array.from(dropdownInputs).map((input) => input.id));
      if (currentInputId && !activeIds.has(currentInputId)) {
        setCurrentInputId(null);
        setAnchorEl(null);
      }
    };
    bindDropdownEvents();
    cleanupRemovedInputs();
    window.bindDropdownEvents = bindDropdownEvents;
    window.cleanupRemovedInputs = cleanupRemovedInputs;
    const observer = new MutationObserver(() => {
      bindDropdownEvents();
      cleanupRemovedInputs();
    });
    const froalaEditor = document.querySelector(".fr-element");
    if (froalaEditor) {
      observer.observe(froalaEditor, { childList: true, subtree: true });
    }
    return () => {
      observer.disconnect();
      const functionName = `openDropdown_${instanceId.current} `;
      delete window[functionName];
    };
  }, [dialogOptionList, summaryOptionList, editorData]);

  const handleSelect = (value) => {
    if (!currentInputId) return;
    // Prevent the input's blur handler from overwriting the selected value with the
    // previously typed text while/after this selection is applied.
    suppressBlurPersistRef.current = true;
    const safeValue = value.replace(/"/g, '&quot;');
    const inputElement = document.getElementById(currentInputId);
    if (inputElement) {
      inputElement.value = safeValue;
      inputElement.setAttribute("value", safeValue);
      inputElement.setAttribute("title", safeValue);
    }
    const editorInstance = editor?.current?.editor;
    let newContent;
    if (editorInstance) {
      newContent = editorInstance.html.get().replace(
        new RegExp(`(<input[\\s\\S]*?id=["']${currentInputId}["'][\\s\\S]*?value=["'])([\\s\\S]*?)(["'])`, 'i'), `$1${safeValue}$3`
      );
      editorInstance.html.set(newContent);
      editorInstance.events.trigger('contentChanged');
    }
    let updatedDropdownList = [];
    if (id === "intro-text-editor") {
      // Prefer editorData?.dropdownList as it reflects newly-added dropdowns;
      // fall back to commentData if the local list is empty.
      const rawIntro = commentData?.introductory_text_dropdowns;
      const introArr = Array.isArray(rawIntro) ? rawIntro
        : Array.isArray(rawIntro?.introductory_text_dropdowns) ? rawIntro.introductory_text_dropdowns : [];
      const currentList = editorData?.dropdownList?.length > 0 ? editorData.dropdownList : introArr;
      updatedDropdownList = currentList.map((dropdown) =>
        dropdown?.dropdown_uuid === currentInputId
          ? { ...dropdown, selected_value: value }
          : dropdown
      );
      if (!updatedDropdownList.some((d) => d?.dropdown_uuid === currentInputId)) {
        updatedDropdownList.push({ dropdown_uuid: currentInputId, selected_value: value, name: "", dropdowns_values: [] });
      }
    }
    else if (dropdownList?.length > 0) {
      updatedDropdownList = dropdownList?.map((dropdown) =>
        dropdown?.dropdown_uuid === currentInputId
          ? { ...dropdown, selected_value: value }
          : dropdown
      );
    } else {
      updatedDropdownList = editorData?.dropdownList?.map((dropdown) =>
        dropdown?.dropdown_uuid === currentInputId
          ? { ...dropdown, selected_value: value }
          : dropdown
      );
    }
    const effectiveId = (id === "" && setCommentText) ? "commentText" : id;
    handleDropdownList?.(effectiveId, updatedDropdownList, "select", currentInputId);
    // Sync innerHTML too (not just dropdownList) so a re-render can't restore the
    // previously typed value over the chosen selection.
    const applyState = (setter) => setter?.((prevState) => ({
      ...prevState,
      ...(newContent !== undefined ? { innerHTML: newContent } : {}),
      dropdownList: updatedDropdownList,
    }));
    if (effectiveId === "commentText") {
      applyState(setCommentText);
    } else if (effectiveId === "summaryText") {
      applyState(setCommentSummary);
    } else if (effectiveId === "non-narrative-comment" || effectiveId === "intro-text-editor") {
      applyState(setEditorContent);
    }
    setState((prevState) => ({ ...prevState, dropdownList: updatedDropdownList }));
    setAnchorEl(null);
    setCurrentInputId(null);
    // Release the blur suppression after the close-induced blur has settled.
    setTimeout(() => { suppressBlurPersistRef.current = false; }, 150);
  };

  useEffect(() => {
    if (currentInputId && anchorEl) {
      const input = document.getElementById(currentInputId);
      if (input) {
        const newList = editorData?.dropdownList?.find((obj) => obj?.dropdown_uuid === currentInputId);
        const normalizeValues = (v) => Array.isArray(v?.values) ? v.values : (Array.isArray(v) ? v : []);
        const filterValues = (arr) => normalizeValues(arr).filter(v => !v?.toUpperCase?.().includes("XXX"));
        const dropType = input.getAttribute('dropType') ||
          (id === 'summaryText' ? 'summary' :
            (id === 'commentText' || id === 'non-narrative-comment') ? 'comment' :
              (id === 'intro-text-editor' ? 'intro' : 'unknown'));
        let inputList = newList?.dropdowns_values;
        const normalizedList = normalizeValues(inputList);
        if (!normalizedList || normalizedList.length === 0) {
          if (dropType === 'comment') {
            inputList = commentData?.comment_dropdowns?.find((o) => o?.dropdown_uuid === currentInputId)?.dropdowns_values;
          } else if (dropType === 'summary') {
            inputList = commentData?.summary_comment_dropdowns?.find((o) => o?.dropdown_uuid === currentInputId)?.dropdowns_values;
          } else if (dropType === 'intro') {
            const rawIntro2 = commentData?.introductory_text_dropdowns;
            const introArr2 = Array.isArray(rawIntro2) ? rawIntro2
              : Array.isArray(rawIntro2?.introductory_text_dropdowns) ? rawIntro2.introductory_text_dropdowns : [];
            inputList = introArr2.find((o) => o?.dropdown_uuid === currentInputId)?.dropdowns_values;
          } else {
            inputList = dialogOptionList;
          }
        }
        inputList = filterValues(inputList);
        setDropdownOptions(inputList || []);
      }
    }
  }, [commentData, editorData, currentInputId, anchorEl, id, dialogOptionList]);

  const addDropdown = (e, list) => {
    e.stopPropagation();
    e.preventDefault();
    const editorInstance = editor.current.editor;
    const editorRoot = editorInstance.$el.get(0);
    editorRoot.focus();

    const applyInsertionRange = (range) => {
      if (!range) return;
      const sel = window.getSelection();
      sel.removeAllRanges();
      sel.addRange(range);
      try {
        editorInstance.selection.setRange(range);
      } catch (_) {}
    };

    const bookmark = editorRoot.querySelector('[data-cursor-bookmark]');
    if (bookmark) {
      const dynamicWrapper = bookmark.closest(".froala-dynamic-input-wrapper");
      const range = document.createRange();
      if (dynamicWrapper && editorRoot.contains(dynamicWrapper)) {
        const afterWrapper = getRangeAfterElement(dynamicWrapper);
        if (afterWrapper) applyInsertionRange(afterWrapper);
      } else {
        range.setStartBefore(bookmark);
        range.collapse(true);
        applyInsertionRange(range);
      }
      bookmark.remove();
    } else {
      editorInstance.selection.restore();
    }

    const activeInput = document.activeElement;
    if (
      activeInput?.tagName === "INPUT" &&
      activeInput.closest(".froala-dynamic-input-wrapper") &&
      editorRoot.contains(activeInput)
    ) {
      const afterWrapper = getRangeAfterElement(activeInput.closest(".froala-dynamic-input-wrapper"));
      if (afterWrapper) applyInsertionRange(afterWrapper);
    } else {
      const sel = window.getSelection();
      if (sel?.rangeCount > 0) {
        applyInsertionRange(
          normalizeRangeOutsideDynamicInputWrapper(sel.getRangeAt(0), editorRoot)
        );
      }
    }

    const selection = editorInstance.selection.get();
    if (!selection) return;
    const selectedNode = selection.startContainer || selection.commonAncestorContainer;
    const dropdownAncestor = editorInstance.$(selectedNode).closest('span[id^="dropdown_"]')[0];
    if (dropdownAncestor) {
      const range = editorInstance.selection.ranges(0);
      if (range) {
        const afterNode = document.createTextNode("\u00A0");
        dropdownAncestor.parentNode.insertBefore(afterNode, dropdownAncestor.nextSibling);
        range.setStartAfter(afterNode);
        range.setEndAfter(afterNode);
        editorInstance.selection.setRange(range);
      }
    }
    let newDropdownList = editorData?.dropdownList?.length ? editorData?.dropdownList : dropdownList?.length ? [...dropdownList] : [];
    newDropdownList = newDropdownList?.length ? newDropdownList : id === "intro-text-editor" ? normalizeDropdowns(commentData?.introductory_text_dropdowns) : [];
    newDropdownList = newDropdownList?.length ? newDropdownList : [];
    const newSelectUuid = uuid();
    const html = editorInstance.html.get();
    if (html.trim() === "<p><br></p>") {
      editorInstance.html.set("");
    }

    const insertRange =
      editorInstance.selection.ranges?.(0) ||
      (window.getSelection()?.rangeCount > 0 ? window.getSelection().getRangeAt(0) : null);
    const targetListItem = resolveTargetListItem(editorRoot, insertRange, editorInstance);
    const isEmptyListInsert = targetListItem && isListItemEffectivelyEmpty(targetListItem);
    if (isEmptyListInsert) {
      targetListItem.innerHTML = "";
    }
    const preservedBrNodes =
      isEmptyListInsert || !insertRange
        ? new Set()
        : getPreservedBrNodesAtInsertPoint(insertRange);

    const scrollContainer = editorInstance.$el.get(0).closest('.fr-wrapper') || editorInstance.$el.get(0).closest('.fr-scroller') || window;
    const scrollTop = scrollContainer.scrollTop ?? window.scrollY;
    editorInstance.html.insert(
      `<span contenteditable="false" id="dropdown_${newSelectUuid}" class="froala-dropdown-wrapper" style="display:inline-flex;align-items:center;">
          <input
            type="text"
            class="froala-dropdown-input"
            id="${newSelectUuid}"
            name="${id}"
            value=""
            placeholder="Type or select from dropdown"
            style="
              width: 220px;
              background-color: #f1f9fe;
              height: 32px;
              padding: 4px 8px;
              vertical-align: middle;
              border-top: 1px solid #4282e2;
              border-bottom: 1px solid #4282e2;
              border-left: 1px solid #4282e2;
              border-right: none;
              border-radius: 4px 0 0 4px;
              font-size: 14px;
              font-weight: 600;
              color: #3B3B3B;
              margin-left: 2px;
              cursor: text;
              text-align: left;
            "
            autocomplete="off"
            autocorrect="off"
            spellcheck="false"
          />
          <button
            type="button"
            id="dropdownIcon_${newSelectUuid}"
            name="${id}"
            data-dropdown-id="${newSelectUuid}"
            onclick="window.openDropdown('${newSelectUuid}', event)"
            style="background-color: #f1f9fe; width: 23px; height: 23px;
              border: 1px solid #4282e2; border-left: 1px solid #4282e2;
              font-size: 14px; color: #4282e2;
              ${!rwSettingAccess && open === "global_lists" ? `border-radius: 0 4px 4px 0;` : ""}
              display: inline-flex; align-items: center; justify-content: center;
              cursor: pointer;"
          >▾</button>
         ${rwSettingAccess || open !== "global_lists" ? `<button
            id="dropdownButton${newSelectUuid}"
            name="${id}"
            style="
              background-color: #f1f9fe;
              width: 24px;
              height: 23px;
              border: 1px solid #4282e2;
              border-radius: 0 4px 4px 0;
              margin-left: -0.5px;
              font-size: 14px;
              color: #4282e2;
              display: inline-flex;
              align-items: center;
              justify-content: center;
              cursor: pointer;
            "
          >✎</button>` : ""}
        </span>&nbsp;`,
      true
    );

    const insertedDropdown = editorRoot.querySelector(`#dropdown_${newSelectUuid}`);
    const dropdownWrapper = insertedDropdown?.closest('span[id^="dropdown_"]');
    finalizeControlWrapperInsert(dropdownWrapper, preservedBrNodes);
    if (
      insertedDropdown &&
      dropdownWrapper &&
      relocateDropdownOutsideDynamicInputWrapper(dropdownWrapper)
    ) {
      editorInstance.events.trigger("contentChanged");
    }

    if (scrollContainer === window) { window.scrollTo(0, scrollTop); } else { scrollContainer.scrollTop = scrollTop; }
    window.bindDropdownEvents?.();
    window.cleanupRemovedInputs?.();
    let newDropodwnObj = {
      dropdown_uuid: newSelectUuid,
      name: list?.name || "",
      selected_value: "",
      dropdowns_values: list?.options,
    }
    if (open === "global_lists" || (saveToGlobalLists && open === "new_list")) newDropodwnObj.list_guid = list?.list_guid;
    newDropdownList?.push(newDropodwnObj);
    setState((prevState) => ({ ...prevState, dropdownList: newDropdownList }));
    if (setIsFocused) setIsFocused(true);
    if (setHasChanges) setHasChanges(true);
    if (setIsButtonAdded) setIsButtonAdded(true);
    let updatedHtml = editorInstance.html.get();
    updatedHtml = updatedHtml.replace(/<p>\s*<br\s*\/?>\s*<\/p>/gi, '');
    if (id === "intro-text-editor" && setEditorContent) {
      handleDropdownList?.(id, newDropdownList, null, newSelectUuid);
      setEditorContent(prevState => ({ ...prevState, innerHTML: updatedHtml, dropdownList: newDropdownList }));
    } else if (id === "commentText" && setCommentText) {
      setCommentText(prevState => ({ ...prevState, innerHTML: updatedHtml, dropdownList: newDropdownList, comment_dropdowns: newDropdownList }))
    } else if (id === "summaryText" && setCommentSummary) {
      setCommentSummary(prevState => ({ ...prevState, innerHTML: updatedHtml, dropdownList: newDropdownList, summary_comment_dropdowns: newDropdownList }))
    } else if (id === "non-narrative-comment") {
      setEditorContent(prevState => ({ ...prevState, innerHTML: updatedHtml, dropdownList: newDropdownList }));
    }
  };

  const setRangeAfterWrapper = (wrapper) => {
    if (!wrapper?.parentNode || !document.contains(wrapper)) return null;
    return getRangeAfterElement(wrapper);
  };

  const resolveInsertRangeForAddInput = (range, editorRoot, lastFocusedWrapper) => {
    if (!range || !editorRoot) return range;

    const activeInput = document.activeElement;
    if (activeInput?.tagName === "INPUT" && editorRoot.contains(activeInput)) {
      const wrapper = activeInput.closest(".froala-dynamic-input-wrapper");
      if (wrapper) return setRangeAfterWrapper(wrapper) || range;
    }

    const resolved = range.cloneRange();
    resolved.collapse(true);

    let contextEl =
      resolved.startContainer.nodeType === Node.TEXT_NODE
        ? resolved.startContainer.parentElement
        : resolved.startContainer;
    const insideWrapper = contextEl?.closest?.(".froala-dynamic-input-wrapper");
    if (insideWrapper) return setRangeAfterWrapper(insideWrapper) || resolved;

    const li = contextEl?.closest?.("li");
    if (li && li.querySelector(".froala-dynamic-input-wrapper")) {
      let atLiStart = false;
      try {
        const atStart = document.createRange();
        atStart.setStart(li, 0);
        atStart.collapse(true);
        atLiStart = resolved.compareBoundaryPoints(Range.START_TO_START, atStart) === 0;
      } catch (_) {}

      if (atLiStart) {
        if (lastFocusedWrapper?.closest?.("li") === li) {
          return setRangeAfterWrapper(lastFocusedWrapper) || resolved;
        }
        const wrappers = li.querySelectorAll(".froala-dynamic-input-wrapper");
        if (wrappers.length) {
          return setRangeAfterWrapper(wrappers[wrappers.length - 1]) || resolved;
        }
      }
    }

    if (resolved.startContainer.nodeType === Node.ELEMENT_NODE) {
      const next = resolved.startContainer.childNodes[resolved.startOffset];
      if (
        next?.classList?.contains?.("froala-dynamic-input-wrapper") &&
        lastFocusedWrapper &&
        li
      ) {
        return setRangeAfterWrapper(lastFocusedWrapper) || resolved;
      }
    }

    return normalizeRangeForLineBreakInsert(resolved);
  };

  const applyEditorRange = (range) => {
    if (!range) return null;
    try {
      const sel = window.getSelection();
      sel.removeAllRanges();
      sel.addRange(range);
      savedRangeRef.current = range.cloneRange();
      return range.cloneRange();
    } catch (_) {
      return null;
    }
  };

  const resolveAnchorWrapper = (anchorWrapper, editorRoot) => {
    if (anchorWrapper?.parentNode && document.contains(anchorWrapper)) {
      return anchorWrapper;
    }
    const inputId = anchorWrapper?.querySelector?.("input")?.id;
    if (!inputId) return null;
    const input = editorRoot.querySelector(`#${CSS.escape(inputId)}`);
    const resolved = input?.closest?.(".froala-dynamic-input-wrapper") || null;
    return resolved?.parentNode && document.contains(resolved) ? resolved : null;
  };

  const placeFallbackInsertionRange = (editorInstance, editorRoot, contextRange) => {
    editorInstance.$el.get(0).focus();
    const sel = window.getSelection();

    if (contextRange && isRangeInEditor(contextRange, editorRoot)) {
      const normalized = normalizeOutsideListInsertRange(editorRoot, contextRange);
      sel.removeAllRanges();
      sel.addRange(normalized);
      savedRangeRef.current = normalized.cloneRange();
      return normalized.cloneRange();
    }

    const contextLi = getListItemFromRange(contextRange);
    let lastWrapper = null;
    if (contextLi) {
      const liWrappers = contextLi.querySelectorAll(".froala-dynamic-input-wrapper");
      lastWrapper = liWrappers.length ? liWrappers[liWrappers.length - 1] : null;
    }
    if (!lastWrapper) {
      const allWrappers = editorRoot.querySelectorAll(".froala-dynamic-input-wrapper");
      lastWrapper = allWrappers.length ? allWrappers[allWrappers.length - 1] : null;
    }
    const fallbackRange = document.createRange();

    if (lastWrapper?.parentNode && document.contains(lastWrapper)) {
      const afterWrapper = getRangeAfterElement(lastWrapper);
      if (afterWrapper) {
        fallbackRange.setStart(afterWrapper.startContainer, afterWrapper.startOffset);
        fallbackRange.collapse(true);
      } else {
        fallbackRange.setStartAfter(lastWrapper);
        fallbackRange.collapse(true);
      }
    } else {
      editorInstance.selection.setAtEnd(editorRoot);
      editorInstance.selection.restore();
      if (sel?.rangeCount > 0 && isRangeInEditor(sel.getRangeAt(0), editorRoot)) {
        savedRangeRef.current = sel.getRangeAt(0).cloneRange();
        return sel.getRangeAt(0).cloneRange();
      }
      fallbackRange.selectNodeContents(editorRoot);
      fallbackRange.collapse(false);
    }

    sel.removeAllRanges();
    sel.addRange(fallbackRange);
    savedRangeRef.current = fallbackRange.cloneRange();
    return fallbackRange.cloneRange();
  };

  const ensureInsertionRangeInEditor = (editorInstance, editorRoot, savedRange, anchorWrapper) => {
    editorInstance.$el.get(0).focus();
    const sel = window.getSelection();
    const caretLi = getListItemFromRange(savedRange);
    const anchorLi = anchorWrapper?.closest?.("li") || null;
    const anchorMatchesCaret = !caretLi || (anchorLi && anchorLi === caretLi);

    if (savedRange && isRangeInEditor(savedRange, editorRoot)) {
      const range = shouldInsertOutsideList(editorRoot, savedRange)
        ? normalizeOutsideListInsertRange(editorRoot, savedRange)
        : savedRange.cloneRange();
      sel.removeAllRanges();
      sel.addRange(range);
      return range.cloneRange();
    }

    if (sel?.rangeCount > 0 && isRangeInEditor(sel.getRangeAt(0), editorRoot)) {
      return sel.getRangeAt(0).cloneRange();
    }

    const resolvedAnchor = resolveAnchorWrapper(anchorWrapper, editorRoot);
    if (resolvedAnchor && anchorMatchesCaret) {
      const afterAnchorRange = setRangeAfterWrapper(resolvedAnchor);
      if (afterAnchorRange) {
        const applied = applyEditorRange(afterAnchorRange);
        if (applied) return applied;
      }
    }

    return placeFallbackInsertionRange(editorInstance, editorRoot, savedRange);
  };

  const addInput = (e) => {
  e.preventDefault();
  e.stopPropagation();

  const editorInstance = editor.current?.editor;

  if (!editorInstance) return;

  suppressModelChangeRef.current = true;

  const editorRoot = editorInstance.$el.get(0);
  ensureParagraphContextForInsert(editorInstance, editorRoot);
  const clickSavedRange = isRangeInEditor(addInputClickRangeRef.current, editorRoot)
    ? addInputClickRangeRef.current.cloneRange()
    : null;
  addInputClickRangeRef.current = null;

  const savedRange =
    clickSavedRange ||
    (isRangeInEditor(savedRangeRef.current, editorRoot)
      ? savedRangeRef.current.cloneRange()
      : null);

  // Only preserve user <br> tags when the caret was already in the editor.
  // Unfocused inserts use Froala's empty-editor <br> placeholder, which should be removed.
  const preserveAdjacentBr = !!savedRange;

  let insertOutsideList = isCaretAfterList(editorRoot, savedRange);

  if (insertOutsideList) {
    lastFocusedDropdownRef.current = null;
  }

  const targetListItemEarly = insertOutsideList
    ? null
    : resolveTargetListItem(editorRoot, savedRange, editorInstance);

  // Check tracked ref first, fall back to activeElement (covers the initial focus case).
  // Anchor after a focused dropdown wrapper, or after a focused dynamic input wrapper
  // (so consecutive "Add input" clicks insert after the previously added input).
  const anchorWrapperCandidate =
    lastFocusedDropdownRef.current ||
    lastFocusedDynamicWrapperRef.current ||
    document.activeElement?.closest?.('span[id^="dropdown_"]') ||
    document.activeElement?.closest?.('.froala-dynamic-input-wrapper');

  // Only use the candidate if it is still attached to the live DOM; after a
  // previous insert Froala may have replaced the node, leaving the ref stale.
  const anchorWrapper = anchorWrapperCandidate && document.contains(anchorWrapperCandidate)
    ? anchorWrapperCandidate
    : null;

  const anchorWrapperInList = anchorWrapper?.closest?.("li");
  const caretListItemEarly = getListItemFromRange(savedRange);
  const anchorForInsert =
    !savedRange &&
    anchorWrapper &&
    (anchorWrapperInList || !targetListItemEarly)
      ? anchorWrapper
      : null;

  if (!anchorForInsert && !savedRange) {
    lastFocusedDropdownRef.current = null;
    lastFocusedDynamicWrapperRef.current = null;
  }

  const newInputUuid = uuid();
  const lastFocusedWrapperForInsert = lastFocusedDynamicWrapperRef.current;

  let rangeForListInsert = ensureInsertionRangeInEditor(
    editorInstance,
    editorRoot,
    savedRange,
    anchorForInsert
  );

  insertOutsideList =
    shouldInsertOutsideList(editorRoot, rangeForListInsert) ||
    shouldInsertOutsideList(editorRoot, savedRange);

  const caretLiForInsert = getListItemFromRange(savedRange) || getListItemFromRange(rangeForListInsert);
  if (caretLiForInsert) {
    insertOutsideList = false;
  }

  if (insertOutsideList && rangeForListInsert) {
    rangeForListInsert = normalizeOutsideListInsertRange(editorRoot, rangeForListInsert);
    lastFocusedDropdownRef.current = null;
  } else if (
    !insertOutsideList &&
    savedRange &&
    isRangeInEditor(savedRange, editorRoot) &&
    getListItemFromRange(savedRange)
  ) {
    rangeForListInsert = savedRange.cloneRange();
  }

  if (rangeForListInsert) {
    rangeForListInsert = resolveInsertRangeForAddInput(
      rangeForListInsert,
      editorRoot,
      lastFocusedWrapperForInsert
    );
  }

  const insertionParagraph = getAncestorFromRange(
    rangeForListInsert || savedRange,
    "p"
  );

  const caretListItem = getListItemFromRange(rangeForListInsert) || caretListItemEarly;
  const targetListItem = insertOutsideList
    ? null
    : caretListItem ||
      resolveTargetListItem(editorRoot, rangeForListInsert, editorInstance) ||
      targetListItemEarly ||
      (!caretListItem && anchorWrapperInList ? anchorWrapperInList : null);

  const preservedBrForInsert = new Set();
  const capturePreservedBr = (range) => {
    if (!preserveAdjacentBr) return;
    getPreservedBrNodesAtInsertPoint(range).forEach((br) => preservedBrForInsert.add(br));
  };
  capturePreservedBr(rangeForListInsert || savedRange);

  const hasExistingControls = editorRoot.querySelector(
    ".froala-dynamic-input-wrapper, span[id^='dropdown_']"
  );
  if (!hasExistingControls) {
    ensureParagraphContextForInsert(editorInstance, editorRoot);
    if (!isRangeInEditor(rangeForListInsert, editorRoot)) {
      rangeForListInsert = placeFallbackInsertionRange(editorInstance, editorRoot, savedRange);
      capturePreservedBr(rangeForListInsert);
    }
  }

  let insertedInList = false;
  const caretInListItem = getListItemFromRange(rangeForListInsert);
  const canInsertAtDomRange =
    rangeForListInsert &&
    document.contains(rangeForListInsert.startContainer) &&
    !caretInListItem &&
    !targetListItem;

  if (
    (insertOutsideList || canInsertAtDomRange) &&
    rangeForListInsert &&
    document.contains(rangeForListInsert.startContainer)
  ) {
    try {
      insertDynamicInputAtRange(newInputUuid, rangeForListInsert, editorInstance, preserveAdjacentBr);
      insertedInList = true;
    } catch (_) {}
  } else if (
    targetListItem &&
    editorRoot.contains(targetListItem)
  ) {
    try {
      insertDynamicInputInListItem(
        targetListItem,
        newInputUuid,
        rangeForListInsert,
        editorInstance,
        preserveAdjacentBr
      );
      insertedInList = true;
    } catch (_) {}
  }

  if (!insertedInList) {
    if (!isRangeInEditor(rangeForListInsert, editorRoot)) {
      rangeForListInsert = placeFallbackInsertionRange(editorInstance, editorRoot, savedRange);
      capturePreservedBr(rangeForListInsert);
    }
    if (!hasExistingControls) {
      ensureParagraphContextForInsert(editorInstance, editorRoot);
      if (!isRangeInEditor(rangeForListInsert, editorRoot)) {
        rangeForListInsert = placeFallbackInsertionRange(editorInstance, editorRoot, savedRange);
        capturePreservedBr(rangeForListInsert);
      }
    }
    if (
      rangeForListInsert &&
      document.contains(rangeForListInsert.startContainer)
    ) {
      try {
        insertDynamicInputAtRange(newInputUuid, rangeForListInsert, editorInstance, preserveAdjacentBr);
        insertedInList = true;
      } catch (_) {}
    }
    if (!insertedInList) {
      let paragraph = editorRoot.querySelector("p");
      if (!paragraph) {
        paragraph = document.createElement("p");
        paragraph.innerHTML = "<br>";
        editorRoot.appendChild(paragraph);
      }
      const fallbackRange = document.createRange();
      fallbackRange.selectNodeContents(paragraph);
      fallbackRange.collapse(false);
      insertDynamicInputAtRange(newInputUuid, fallbackRange, editorInstance, preserveAdjacentBr);
    }
  }

  removeInvalidEditorBlockElements(editorRoot);

  const newlyInsertedInputEarly = editorRoot.querySelector(`[id="${newInputUuid}"]`);
  const newWrapperEarly = newlyInsertedInputEarly?.closest(".froala-dynamic-input-wrapper");
  if (newWrapperEarly) {
    lastFocusedDynamicWrapperRef.current = newWrapperEarly;
    const afterInsertRange = setRangeAfterWrapper(newWrapperEarly);
    if (afterInsertRange) {
      savedRangeRef.current = afterInsertRange.cloneRange();
      addInputClickRangeRef.current = afterInsertRange.cloneRange();
    }
  }

  // When Froala inserts into an unfocused editor it may wrap the span in a <p>, or place
  // it as a sibling of <ol>/<ul>. Move the wrapper inside the adjacent list item.
  const newlyInsertedInput = editorRoot.querySelector(`[id="${newInputUuid}"]`);
  if (newlyInsertedInput) {
    const newWrapper = newlyInsertedInput.closest(".froala-dynamic-input-wrapper");
    if (newWrapper) {
      if (!newWrapper.closest("li") && !insertOutsideList && !isNodePositionAfterList(editorRoot, newWrapper)) {
        if (relocateWrapperIntoAdjacentList(newWrapper, editorRoot)) {
          editorInstance.events.trigger("contentChanged");
        }
      }
      const parentEl = newWrapper.parentElement;
      const keepInsideParagraph =
        parentEl?.tagName === "P" &&
        insertionParagraph &&
        parentEl === insertionParagraph;
      if (isFroalaOrphanBlockWrapper(parentEl) && !keepInsideParagraph) {
        const liParent = parentEl.closest("li");
        if (liParent) {
          liParent.insertBefore(newWrapper, parentEl);
          liParent.insertBefore(document.createTextNode("\u00A0"), parentEl);
          const pContent = parentEl.innerHTML.replace(/&nbsp;|<br\s*\/?>/gi, "").trim();
          if (!pContent) parentEl.remove();
        } else if (
          !insertOutsideList &&
          targetListItem &&
          editorRoot.contains(targetListItem) &&
          !targetListItem.contains(newWrapper)
        ) {
          if (isListItemEffectivelyEmpty(targetListItem)) targetListItem.innerHTML = "";
          targetListItem.appendChild(newWrapper);
          targetListItem.appendChild(document.createTextNode("\u00A0"));
          const orphanP = isFroalaOrphanBlockWrapper(newWrapper.parentElement)
            ? newWrapper.parentElement
            : null;
          if (orphanP) {
            const orphanContent = orphanP.innerHTML.replace(/&nbsp;|<br\s*\/?>/gi, "").trim();
            if (!orphanContent) orphanP.remove();
          }
        } else if (parentEl.parentNode && editorRoot.contains(parentEl)) {
          parentEl.parentNode.insertBefore(newWrapper, parentEl);
          parentEl.parentNode.insertBefore(document.createTextNode("\u00A0"), parentEl);
          const pContent = parentEl.innerHTML.replace(/&nbsp;|<br\s*\/?>/gi, "").trim();
          if (!pContent) parentEl.remove();
        }
      } else if (
        !newWrapper.closest("li") &&
        !insertOutsideList &&
        !isNodePositionAfterList(editorRoot, newWrapper)
      ) {
        if (relocateWrapperIntoAdjacentList(newWrapper, editorRoot)) {
          editorInstance.events.trigger("contentChanged");
        }
      }
    }
  }
  removeEmptyEditorBlocks(editorRoot);
  removeInvalidEditorBlockElements(editorRoot);

  const finalInsertedWrapper = editorRoot.querySelector(`[id="${newInputUuid}"]`)?.closest(".froala-dynamic-input-wrapper");
  if (finalInsertedWrapper) {
    finalizeControlWrapperInsert(finalInsertedWrapper, preservedBrForInsert);
    normalizeWrapperBlockToParagraph(finalInsertedWrapper, editorRoot);
  }
  relocateDynamicInputsIntoEditorRoot(editorRoot);
  normalizeDynamicInputSeparators(editorRoot);

  // State updates — mark local edit before any async save so parent state is not overwritten.
  onLocalEditStart?.();
  setHasChanges?.(true);
  setIsFocused?.(true);
  setIsButtonAdded?.(true);

  syncDynamicInputValuesToAttributes(editorRoot);
  const rawEditorContent = editorInstance.html.get();
  const editorContent = stripUndefinedTagsFromHtml(rawEditorContent);
  if (editorContent !== rawEditorContent) {
    editorInstance.html.set(editorContent);
  }

  if (setEditorContent) {
    setEditorContent(prev => ({
      ...prev,
      innerHTML: editorContent,
    }));
  } else if (id === "commentText" && setCommentText) {
    setCommentText(prev => ({
      ...prev,
      innerHTML: editorContent,
    }));
  } else if (id === "summaryText" && setCommentSummary) {
    setCommentSummary(prev => ({
      ...prev,
      innerHTML: editorContent,
    }));
  }

  // Focus input and attach listeners after DOM update
  requestAnimationFrame(() => {
    const inputEl = document.getElementById(newInputUuid);
    const editorRoot = editorInstance.$el?.get(0);

    if (!inputEl) {
      suppressModelChangeRef.current = false;
      return;
    }

    inputEl.focus({ preventScroll: true });
    inputEl.select();

    const newWrapper = inputEl.closest(".froala-dynamic-input-wrapper");
    if (newWrapper) {
      lastFocusedDynamicWrapperRef.current = newWrapper;
      const afterRange = setRangeAfterWrapper(newWrapper);
      if (afterRange) savedRangeRef.current = afterRange.cloneRange();
    }

    suppressModelChangeRef.current = false;

    const handleChange = (event) => {
      event.stopPropagation();
      const val = event.target.value.replace(/"/g, "&quot;").trim();
      event.target.setAttribute("value", val);
      event.target.setAttribute("title", val);
      setHasChanges?.(true);
      const wrapper = inputEl.closest(".froala-dynamic-input-wrapper");
      if (wrapper) {
        try {
          const range = buildRangeAfterWrapper(wrapper);
          if (range) {
            savedRangeRef.current = range.cloneRange();
            lastFocusedDynamicWrapperRef.current = wrapper;
          }
        } catch (_) {}
      }
      document.dispatchEvent(
        new CustomEvent("changeInput", {
          detail: { id: newInputUuid, value: event.target.value },
        })
      );
    };

    // When the input is empty and Backspace is pressed, remove the wrapper from the editor
    const handleKeydown = (keyEvent) => {
      if (keyEvent.key === "Backspace" && inputEl.value === "") {
        keyEvent.preventDefault();
        const wrapper = inputEl.closest(".froala-dynamic-input-wrapper");
        if (wrapper) {
          detachDynamicInputListeners(newInputUuid);
          const anchorNode = wrapper.previousSibling;
          const parentNode = wrapper.parentNode;
          wrapper.remove();
          const activeEditor = editor.current?.editor;
          if (activeEditor) {
            normalizeDynamicInputSeparators(activeEditor.$el?.get(0));
            placeCaretInEditorAt(anchorNode, parentNode);
            activeEditor.events.trigger("contentChanged");
            const updatedContent = activeEditor.html.get();
            if (setEditorContent) setEditorContent(prev => ({ ...prev, innerHTML: updatedContent }));
            else if (id === "commentText" && setCommentText) setCommentText(prev => ({ ...prev, innerHTML: updatedContent }));
            else if (id === "summaryText" && setCommentSummary) setCommentSummary(prev => ({ ...prev, innerHTML: updatedContent }));
          }
        }
      }
    };

    ensureDynamicInputObserver(editorRoot);
    registerDynamicInputListeners(inputEl, newInputUuid, handleKeydown, handleChange);
  });
};

  const updateCurrentDropdown = (newId) => {
    const newDialogOptions = dialogOptionList.filter(s => s?.trim())
    const inputId = selectedDropdown?.replace("dropdownButton", "").replace("dropdownIcon_", "");
    if (setHasChanges) setHasChanges(true);
    let newDropdownOptions = dropdownList?.length
      ? [...dropdownList]
      : id === "intro-text-editor"
        ? commentData?.introductory_text_dropdowns
        : editorData?.dropdownList || [];
    let index = newDropdownOptions?.findIndex((obj) => obj?.dropdown_uuid === inputId);
    if (index === -1) index = dropdownList?.findIndex((obj) => obj?.isPasted === true);
    const editorInstance = editor?.current?.editor;
    let editorContent = editorInstance?.html?.get();
    const newDialogOptionList = newDialogOptions?.filter((option) => option.trim() !== "");
    if (newDropdownOptions.length === 0) {
      const oldDropdown = dropdownList?.find((obj) => obj?.dropdown_uuid === inputId) ||
        (id === "intro-text-editor" ? normalizeDropdowns(commentData?.introductory_text_dropdowns)?.find((obj) => obj?.dropdown_uuid === inputId) : null) ||
        editorData?.dropdownList?.find((obj) => obj?.dropdown_uuid === inputId);
      newDropdownOptions.push({
        list_guid: newId || undefined,
        dropdown_uuid: newId || inputId,
        selected_value: oldDropdown?.selected_value || "",
        dropdowns_values: newDialogOptionList?.length === 0 ? [] : newDialogOptionList
      });
    } else {
      newDropdownOptions[index] = {
        ...newDropdownOptions[index],
        list_guid: newId || undefined,
        dropdowns_values: newDialogOptionList || [],
      };
    }
    return { newDropdownOptions, editorContent, newDialogOptionList };
  };

  const updateEditorStates = (newDropdownOptions, editorContent, newDialogOptionList) => {
    if (setEditorContent && id === "intro-text-editor") {
      handleDropdownList?.(id, newDropdownOptions);
      setEditorContent(prevState => ({ ...prevState, innerHTML: editorContent, dropdownList: newDropdownOptions }));
    }
    else if (id === "commentText" && setCommentText) {
      setCommentText(prevState => ({ ...prevState, innerHTML: editorContent, dropdownList: newDropdownOptions, selectedDropdownValue: newDialogOptionList }));
    }
    else if (id === "summaryText" && setCommentSummary) {
      setCommentSummary(prevState => ({ ...prevState, innerHTML: editorContent, dropdownList: newDropdownOptions, selectedDropdownValue: newDialogOptionList }));
    }
    else if (id === "non-narrative-comment") setEditorContent(prevState => ({ ...prevState, innerHTML: editorContent, dropdownList: newDropdownOptions }));
  };

  const resetState = () => {
    setState((prevState) => ({
      ...prevState,
      name: "",
      open: false,
      description: "",
      tags: [],
      dialogOptionList: [],
      saveToGlobalLists: false,
      newOption: "",
      textfield: false,
      editedOption: null,
      dropdownList: [],
      summaryOptionList: [],
      listGuid: null,
      listId: null
    }));
  };

  const handleSave = async (event) => {
    const newDialogOptionList = dialogOptionList.filter(s => s?.trim())
    event.stopPropagation();
    let userId = sessionStorage.getItem("UID");
    if (!saveToGlobalLists && open === "new_list") {
      addDropdown(event, { options: newDialogOptionList });
      setState((prevState) => ({ ...prevState, name: "", open: false, description: "", tags: [], dialogOptionList: [] }));
    }
    else if (open === "edit_list" && saveToGlobalLists) {
      const newUuid = uuid();
      const data = {
        options: newDialogOptionList,
        list_guid: listGuid || newUuid,
        name: name?.length > 255 ? name?.slice(0, 255) : name,
        description: description,
        user_id: userId,
        tags: tags
      }
      if (listGuid) {
        await axios.put(`${baseUrl}update-list/${listGuid}`, { ...data, tags: tags?.map((tag) => tag?.id || tag?.pivot?.tag_id || []) }, {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${Base64.decode(sessionSecretKey)}`,
            "api-key": `${Base64.decode(sessionAPIKey)}`,
            platform: "web",
            "api-platform": "WRW",
          }
        });
        const { newDropdownOptions, editorContent, newDialogOptionList } = updateCurrentDropdown(listGuid);
        updateEditorStates(newDropdownOptions, editorContent, newDialogOptionList);
      } else {
        await axios.post(`${baseUrl}lists`, { ...data, tags: tags?.map((tag) => tag?.id || tag?.pivot?.tag_id) }, {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${Base64.decode(sessionSecretKey)}`,
            "api-key": `${Base64.decode(sessionAPIKey)}`,
            platform: "web",
            "api-platform": "WRW",
          }
        });
        const { newDropdownOptions, editorContent, newDialogOptionList } = updateCurrentDropdown(newUuid);
        updateEditorStates(newDropdownOptions, editorContent, newDialogOptionList);
      }
      resetState();
    }
    else if (saveToGlobalLists) {
      const newId = uuid();
      const data = {
        options: newDialogOptionList,
        list_guid: newId,
        name: name?.length > 255 ? name?.slice(0, 255) : name,
        description: description,
        user_id: userId,
        tags: tags
      }
      await axios.post(`${baseUrl}lists`, { ...data, tags: tags?.map((tag) => tag?.id || tag?.pivot?.tag_id) }, {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${Base64.decode(sessionSecretKey)}`,
          "api-key": `${Base64.decode(sessionAPIKey)}`,
          platform: "web",
          "api-platform": "WRW",
        }
      });
      addDropdown(event, data);
      resetState();
    }
    else {
      const { newDropdownOptions, editorContent, newDialogOptionList } = updateCurrentDropdown();
      updateEditorStates(newDropdownOptions, editorContent, newDialogOptionList);
      resetState(newDropdownOptions, newDialogOptionList);
    }
  };

  const openHelpDialog = () => {
    setState((prevState) => ({ ...prevState, openHelp: !openHelp }));
    setShowAIModal(!showAIModal);
  };

  Froalaeditor.DefineIcon("aiHelper", { SRC: BulbIcon, ALT: "Image button", height: 50, width: "50px", template: "image" });
  Froalaeditor.RegisterCommand("aiHelper", {
    title: "AI helper",
    focus: false,
    undo: true,
    refreshAfterCallback: true,
    callback: function () {
      openHelpDialog();
    },
  });

  const toolbarButtonsConfig = {
    moreText: { buttons: ["bold", "italic", "underline", "fontFamily", "fontSize", "textColor", "backgroundColor", "alignLeft", "alignCenter", "alignRight"], align: "left", buttonsVisible: 3 },
    moreRich: { buttons: ["insertLink", "emoticons", "specialCharacters", "fontAwesome", "formatOLSimple", "formatUL", "outdent", "indent"], align: "left", buttonsVisible: 0 },
    moreMisc: { buttons: ["undo", "redo"], align: "right", buttonsVisible: 2 },
  };

  if (showAI) {
    toolbarButtonsConfig.aiHelper = { buttons: ["aiHelper"], align: "left" };
  }

  const handleSetComment = (values) => {
    const editorInstance = editor.current.editor;
    const cursorPosition = editorInstance.selection.get();
    if (cursorPosition) {
      editorInstance.html.insert(`<div><div>${values}</div></div>`);
    }
  };

  const setEditorData = (data, type) => {
    const editorInstance = editor?.current?.editor;
    const parser = new DOMParser();
    const document = parser.parseFromString(editorInstance?.html?.get(), "text/html");
    document.querySelectorAll("span").forEach((span) => {
      const hasSelect = span.querySelector("select");
      const hasButton = span.querySelector("button");
      const otherContent = span.cloneNode(true);
      otherContent.querySelectorAll("select, button, input").forEach((child) => child.remove());
      if (otherContent.innerHTML.trim()) {
        const pTag = document.createElement("p");
        pTag.innerHTML = otherContent.innerHTML;
        span.parentNode.insertBefore(pTag, span.nextSibling);
      }
      if (!hasSelect?.id || !hasButton?.id) {
        hasSelect?.remove();
        hasButton?.remove();
        span.remove();
      }
    });
    document.querySelectorAll("select:not([id]), select[id='isPasted']").forEach((select) => {
      let nextSibling = select.nextElementSibling;
      if (!select?.id) select.remove();
      while (nextSibling && nextSibling.nodeType === 1) {
        if (nextSibling?.tagName.toLowerCase() === 'button') {
          nextSibling.remove();
          break;
        }
        nextSibling = nextSibling.nextElementSibling;
      }
    });
    document.querySelectorAll("p, div, span").forEach((el) => {
      if (!el.textContent.trim()) {
        el.remove();
      }
    });
    const pastedElements = document?.querySelectorAll(`[id^="isPasted"], [id^="pasted/"], #isPasted`);
    if (type === "cancel") {
      if (pastedElements?.length > 0) {
        const pastedDoc = parser.parseFromString(data, "text/html");
        pastedElements.forEach((element) => {
          if (element?.tagName === "BR" || element?.tagName === "P") {
            function findNextSpan(node) {
              while (node) {
                if (node?.tagName === "SPAN" || node?.tagName === "SELECT") return node;
                if (node?.tagName === "P") {
                  const nestedSpan = node?.querySelector("span");
                  if (nestedSpan) return nestedSpan;
                  const nestedSelect = node?.querySelector("select");
                  if (nestedSelect) return nestedSelect;
                }
                node = node.nextElementSibling;
              }
              return null;
            }
            const targetSpan = findNextSpan(element.nextElementSibling);
            if (targetSpan?.tagName === "SPAN") {
              const selectElement = targetSpan?.querySelector("select");
              if (selectElement) {
                selectElement.remove();
              }
            } else if (targetSpan?.tagName === "SELECT") {
              targetSpan.remove();
            }
          }
        })
        pastedDoc.querySelectorAll("select:not([id])").forEach((select) => {
          let nextSibling = select.nextElementSibling;
          if (!select?.id) select.remove();
          while (nextSibling && nextSibling.nodeType === 1) {
            if (nextSibling?.tagName.toLowerCase() === 'button') {
              nextSibling.remove();
              break;
            }
            nextSibling = nextSibling.nextElementSibling;
          }
        });
        pastedDoc.querySelectorAll("button:not([id])").forEach((button) => {
          if (!button?.id) button.remove();
        });
        onChange(id, pastedDoc.body.innerHTML);
        editorInstance?.html?.set(pastedDoc.body.innerHTML);
      } else {
        const newDoc = parser.parseFromString(data, "text/html");
        onChange(id, newDoc.body.innerHTML);
        editorInstance?.html?.set(newDoc.body.innerHTML);
      }
    } else {
      if (pastedElements?.length > 0) {
        pastedElements.forEach((element) => {
          const textContent = element.textContent.trim();
          if (textContent) {
            const textNode = document.createTextNode(textContent);
            element.parentNode.replaceChild(textNode, element);
          } else {
            element.remove();
          }
        });
      }
      onChange(id, document.body.innerHTML);
      editorInstance?.html?.set(document.body.innerHTML);
    }
  };

  const handleCloseRemoveDropdown = () => {
    setEditorData(editorData?.innerHTML, "cancel");
    setState((prevState) => ({ ...prevState, openRemoveDropdown: false }));
  };

  const handleSubmitRemoveDropdown = () => {
    const editorInstance = editor?.current?.editor;
    if (!editorInstance) return;
    const copiedData = state.openRemoveDropdown;
    if (!copiedData) return;
    setEditorData(null, "paste");
    editorInstance.html.insert(copiedData);
    setState((prevState) => ({ ...prevState, openRemoveDropdown: false }));
  };

  useEffect(() => {
    const handleDocumentClick = (event) => {
      if (event.target.id.startsWith("dropdownIcon_")) {
        const editorContainer = document.getElementById(id);
        if (editorContainer && editorContainer.contains(event.target)) {
          let dropdownId = event.target.id.replace("dropdownIcon_", "");
          const functionName = `openDropdown_${instanceId.current}`;
          if (window[functionName]) {
            window[functionName](dropdownId, event);
          } else {
            window.openDropdown(dropdownId, event);
          }
        }
      }
    };
    document.addEventListener("click", handleDocumentClick);
    return () => document.removeEventListener("click", handleDocumentClick);
  }, [id]);

  const handleLinkValidation = function (link, text, attrs) {
    const urlPattern = /^(https?:\/\/)([\w\-]+\.)+[\w\-]+(\/[\w\-._~:/?#[\]@!$&'()*+,;=]*)?$/i;
    const testResult = urlPattern.test(link);
    if (testResult) {
      return true
    } else {
      toast.error("Please enter a valid URL.")
      return false;
    }
  };
  const handleButtonClicks = (e, type) => {
    const grandParent = e.target.parentElement.parentElement;
    const grandParentPrevSibling = grandParent.previousElementSibling;
    if (setActiveEditorId) setActiveEditorId(grandParentPrevSibling?.id);
    setState((prevState) => ({
      ...prevState,
      open: type,
      dialogOptionList: [],
      ...(type === "global_lists"
        ? { globalListsFetchNonce: (prevState.globalListsFetchNonce || 0) + 1 }
        : {}),
    }));
  };

  if (!froalaEditorConfigRef.current) {
    froalaEditorConfigRef.current = {
      key: "zEG4iE3C7A8A4C4A3A4aIVLEABVAYFKc2Cb1MYGH1g1NYVMiG5G4E3A2A1C8C6D4D4C4==",
      spellCheck: true,
      imagePaste: false,
      placeholderText: "",
      htmlUntouched: true,
      emptyBlocks: false,
      charCounterCount: false,
      quickInsertEnabled: false,
      htmlAllowEmpty: true,
      paragraphFormat: { N: "Normal" },
      pastePlain: true,
      pasteDeniedAttrs: ['style', 'class', 'id'],
      pasteDeniedTags: ['style'],
      wordPasteModal: false,
      enter: FroalaEditor.ENTER_P,
      htmlWhitespace: true,
      keepFormatOnDelete: true,
      toolbarButtons: toolbarButtonsConfig,
      htmlAllowedTags: ['input', 'span', 'button', 'select', 'option', 'strong', 'u', 'i', 'em', 'a', 'br', 'p', 'div', 'li', 'ol', 'ul', 'blockquote'],
      linkAutoPrefix: "https://",
      linkAlwaysBlank: true,
      pluginsEnabled: ["wordPaste", "spellChecker", "codeBeautifier", "fontFamily", "fontSize", "colors", "align", "lists"],
      events: {},
      ...froalaConfig,
    };
  }
  froalaEditorConfigRef.current.events = {
            'link.beforeInsert': handleLinkValidation,
            "paste.before": handlePasteBefore,
            "initialized": function () {
              if (attachDropdownListenersInsideFroala) attachDropdownListenersInsideFroala(id, id === "summaryText" ? "summary" : "comment");
              const editor = this;
              const el = editor.$el.get(0);
              attachMissingDynamicInputListeners(el);
              unwrapInvalidEditorBlockElements(el);
              if (!el.querySelector('p') && !el.textContent.trim()) {
                requestAnimationFrame(() => {
                  if (el.querySelector('p') || el.textContent.trim()) return;
                  if (isNonNarrativeCommentEditor(id) && isExternalModalFieldFocused(el, id)) return;
                  if (isNonNarrativeCommentEditor(id)) {
                    runHtmlSetPreservingExternalFocus(editor, "<p></p>", id);
                  } else {
                    setEditorHtml(editor, "<p></p>");
                  }
                });
              }
            },
            "contentChanged": function () {
              if (contentChangedNormalizingRef.current) return;

              const editorInstance = this;
              const editorRoot = editorInstance.$el?.get(0);
              let domMutated = false;
              if (removeEmptyDynamicInputWrappers(editorRoot)) domMutated = true;
              if (unwrapInvalidEditorBlockElements(editorRoot)) domMutated = true;
              if (relocateDynamicInputsIntoEditorRoot(editorRoot)) domMutated = true;
              if (removeEmptyBlocksAfterDynamicInputs(editorRoot)) domMutated = true;
              if (normalizeDynamicInputSeparators(editorRoot)) domMutated = true;
              attachMissingDynamicInputListeners(editorRoot);
              editorRoot?.querySelectorAll('.froala-dynamic-input-wrapper span[id^="dropdown_"]').forEach((dropdown) => {
                if (relocateDropdownOutsideDynamicInputWrapper(dropdown)) {
                  domMutated = true;
                }
              });
              if (domMutated) {
                contentChangedNormalizingRef.current = true;
                try {
                  editorInstance.events.trigger("contentChanged");
                } finally {
                  contentChangedNormalizingRef.current = false;
                }
              }
              if (attachDropdownListenersInsideFroala) attachDropdownListenersInsideFroala(id, id === "summaryText" ? "summary" : "comment");
            },
            'focus': function () {
              if (onFocusEditor) onFocusEditor();
            },
            'blur': function () {
              const editorRoot = this.$el?.get(0);
              const activeEl = document.activeElement;
              if (activeEl?.tagName === "INPUT" && editorRoot?.contains(activeEl)) return;
              if (activeEl?.id === "add-input" || activeEl?.closest?.("#add-input")) return;
              if (editorRoot?.contains(activeEl)) return;
              if (onBlurEditor) onBlurEditor();
              if (setIsFocused) setIsFocused(false);
              let blurDomMutated = false;
              if (relocateDynamicInputsIntoEditorRoot(editorRoot)) blurDomMutated = true;
              if (removeEmptyBlocksAfterDynamicInputs(editorRoot)) blurDomMutated = true;
              if (blurDomMutated) {
                this.events.trigger("contentChanged");
              }
              const sel = window.getSelection();
              if (sel && sel.rangeCount > 0) {
                const range = sel.getRangeAt(0);
                const container = range.commonAncestorContainer;
                const containerEl = container.nodeType === Node.TEXT_NODE ? container.parentNode : container;
                if (editorRoot?.contains(containerEl)) {
                  savedRangeRef.current = range.cloneRange();
                }
              }
            },
            'keyup': function () {
              const sel = window.getSelection();
              if (sel && sel.rangeCount > 0) {
                savedRangeRef.current = sel.getRangeAt(0).cloneRange();
              }
            },
            'mouseup': function (e) {
              const sel = window.getSelection();
              if (!sel || sel.rangeCount === 0) return;
              const range = sel.getRangeAt(0);
              if (!range.collapsed) {
                hadTextSelectionOnMouseupRef.current = true;
                savedRangeRef.current = range.cloneRange();
                return;
              }
              hadTextSelectionOnMouseupRef.current = false;
              savedRangeRef.current = range.cloneRange();
              // Guard: only correct when the click landed on/inside a list element.
              // e.target is the actual DOM hit-test element (most reliable);
              // elementFromPoint is a fallback when target is the editor root.
              const evtTarget = e?.target;
              const pointEl =
                e?.clientX != null && e?.clientY != null
                  ? document.elementFromPoint(e.clientX, e.clientY)
                  : null;
              const probe = evtTarget || pointEl;
              if (isDynamicInputElement(probe) || isDynamicInputElement(document.activeElement)) return;
              const isNearList =
                !!probe?.closest?.('li, ol, ul, .froala-dynamic-input-wrapper');
              if (isNearList) {
                correctCaretBeforeWrapper(sel, range, this.$el.get(0), this, probe, e?.clientX, e?.clientY);
              }
            },
            'commands.after': function (cmd) {
              if (cmd === 'formatUL' || cmd === 'formatOLSimple' || cmd === 'formatOL') {
                const sel = window.getSelection();
                if (sel?.rangeCount > 0) {
                  savedRangeRef.current = sel.getRangeAt(0).cloneRange();
                }
              }
            },
            'keydown': function (e) {
              if (e.key === 'Backspace') {
                const selection = this.selection.element();
                if (selection && selection.innerHTML === '<br>') {
                  e.preventDefault();
                  return;
                }
                // Remove a froala-dynamic-input-wrapper in a single backspace press
                const sel = window.getSelection();
                if (sel && sel.rangeCount > 0) {
                  const range = sel.getRangeAt(0);
                  if (range.collapsed) {
                    const { startContainer, startOffset } = range;
                    let prevNode = startContainer.nodeType === Node.ELEMENT_NODE
                      ? startContainer.childNodes[startOffset - 1]
                      : startOffset === 0 ? startContainer.previousSibling : null;
                    if (prevNode && prevNode.classList?.contains('froala-dynamic-input-wrapper')) {
                      e.preventDefault();
                      const anchorNode = prevNode.previousSibling;
                      const parentNode = prevNode.parentNode;
                      prevNode.remove();
                      normalizeDynamicInputSeparators(this.$el?.get(0));
                      placeCaretInEditorAt(anchorNode, parentNode);
                      this.events.trigger('contentChanged');
                    }
                  }
                }
              }
            },
            'click': function (e) {
              const editorInstance = this;
              const editorRoot = editorInstance.$el.get(0);
              const sel = window.getSelection();

              // mouseup may have had a range while click sees a collapsed caret — skip DOM fixes.
              if (hadTextSelectionOnMouseupRef.current) {
                hadTextSelectionOnMouseupRef.current = false;
                return;
              }

              // Never run click-side DOM mutations while the user is highlighting text.
              if (sel?.rangeCount > 0 && !sel.getRangeAt(0).collapsed) {
                return;
              }

              // First, try to correct the caret if it landed before a wrapper in a list.
              // Do this synchronously BEFORE any other click handling.
              if (sel && sel.rangeCount > 0) {
                const range = sel.getRangeAt(0);
                if (range.collapsed) {
                  const evtTarget = e?.target;
                  const pointEl =
                    e?.clientX != null && e?.clientY != null
                      ? document.elementFromPoint(e.clientX, e.clientY)
                      : null;
                  const probe = evtTarget || pointEl;
                  if (isDynamicInputElement(probe) || isDynamicInputElement(document.activeElement)) return;
                  const isNearList =
                    !!probe?.closest?.('li, ol, ul, .froala-dynamic-input-wrapper');
                  if (isNearList) {
                    correctCaretBeforeWrapper(sel, range, editorRoot, editorInstance, probe, e?.clientX, e?.clientY);
                    // After correcting the caret, check if we're still in a list and return early
                    // to prevent Froala from inserting a <p> tag.
                    const newSel = window.getSelection();
                    if (newSel && newSel.rangeCount > 0) {
                      const newRange = newSel.getRangeAt(0);
                      const newEl = newRange.startContainer.nodeType === Node.TEXT_NODE
                        ? newRange.startContainer.parentNode
                        : newRange.startContainer;
                      if (newEl?.closest?.('li, ul, ol')) return;
                    }
                  }
                }
              }

              const node = editorInstance.selection.element();
              if (editorRoot.querySelector(".froala-dynamic-input-wrapper, span[id^='dropdown_']")) {
                if (removeEmptyBlocksAfterDynamicInputs(editorRoot)) {
                  editorInstance.events.trigger("contentChanged");
                }
              }
              if (!node || node.tagName === 'BODY' || node === editorRoot) {
                // Intro text is initialized with <p></p>; never append paragraphs on click.
                if (id === "intro-text-editor") return;
                // If there's any list or dynamic-input content, don't insert a <p> —
                // doing so inserts at offset 0 and moves the caret to the beginning.
                if (editorRoot.querySelector('li, ul, ol, .froala-dynamic-input-wrapper, .froala-dropdown-input')) return;
                // Also guard via window selection when inside a list.
                const winSel = window.getSelection();
                if (winSel && winSel.rangeCount > 0) {
                  const winRange = winSel.getRangeAt(0);
                  if (!winRange.collapsed) return;
                  const container = winRange.startContainer;
                  const el = container.nodeType === Node.TEXT_NODE ? container.parentNode : container;
                  if (el?.closest?.('li, ul, ol')) return;
                }
                // Editor already has content — do not append an empty paragraph (splits/moves text).
                if (editorRoot.textContent?.trim()) return;
                editorInstance.html.insert('<p></p>');
                const p = editorRoot.querySelector('p:last-child');
                if (p) editorInstance.selection.setAtStart(p);
              }
            },
            'commands.before': function (cmd) {
              const editorInstance = this;
              const selNode = editorInstance.selection.element();
              const winSel = window.getSelection();
              const hasTextSelection =
                winSel?.rangeCount > 0 && !winSel.getRangeAt(0).collapsed;
              if (
                (cmd === 'bold' || cmd === 'italic') &&
                !hasTextSelection &&
                (!selNode || selNode.tagName === 'BODY') &&
                !editorInstance.$el.get(0)?.textContent?.trim()
              ) {
                editorInstance.html.insert('<p></p>');
              }
            },
          };

  return (
    <div id={id}>
      {(Boolean(anchorEl)) ? <Popper
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        placement="bottom-end"
        sx={{
          zIndex: 10000,
          '&[data-popper-placement^="bottom"]': { marginTop: '1px !important' },
          '&[data-popper-placement^="top"]': { marginBottom: '1px !important' }
        }}
        modifiers={[{ name: 'flip', options: { fallbackPlacements: ['bottom-end', 'top-start', 'top-end'] } },
        { name: 'preventOverflow', options: { mainAxis: true, altAxis: true } },
        ]}
        id={`popper-${id}`}
      >
        <ClickAwayListener onClickAway={(event) => {
          if (anchorElRef.current?.contains?.(event.target)) return;
          const editorElement = document.getElementById(id);
          const dropdownTrigger = event.target?.closest?.('button[id^="dropdownIcon_"], button[id^="dropdownButton"]');
          if (dropdownTrigger && editorElement?.contains(dropdownTrigger)) return;
          setAnchorEl(null);
          setCurrentInputId(null);
          setDropdownOptions([]);
        }}>
          <Paper
            key={`dropdown-${id}-${currentInputId}`}
            style={{ width: "242px", maxHeight: "400px", overflowY: "auto", overflowX: "hidden", textOverflow: "ellipsis", overflow: isDropdownLoading ? "hidden" : "auto", textAlign: isDropdownLoading ? "center" : null }}
          >
            {isDropdownLoading ? <CircularProgress /> : dropdownOptions?.length === 0 ? <MenuItem sx={{ fontSize: "14px", padding: "12px 4px 12px 8px", fontFamily: "Open Sans", letterSpacing: 0 }}>{"     "}</MenuItem> :
              dropdownOptions?.map((e, index) => (
                <MenuItem key={`${currentInputId}-${index}-${e}`} sx={{
                  fontSize: "14px",
                  padding: "5px 4px 5px 8px",
                  fontFamily: "Open Sans",
                  letterSpacing: 0,
                  color: "#3B3B3B",
                  overflow: "hidden",
                  whiteSpace: "nowrap",
                  textOverflow: "ellipsis",
                  maxWidth: "241px",
                  display: "block"
                }} onClick={() => handleSelect(e)}>{String(e)}</MenuItem>
              ))
            }
          </Paper>
        </ClickAwayListener>
      </Popper> : null}
     <FroalaEditor
        id={id}
        ref={editor}
        value={editorData?.innerHTML}
        onModelChange={handleModelChange}
        config={froalaEditorConfigRef.current}
      />
      <AICommentHelper
        categoryName={categoryName}
        categoryNameFromLocation={categoryNameFromLocation}
        showAIModal={showAIModal}
        setShowAIModal={setShowAIModal}
        id={id}
        editor={editor}
        setCommentText={setCommentText}
        commentTitle={commentTitle}
        open={openHelp}
        onClose={openHelpDialog}
        handleSetComment={handleSetComment}
      />
      {(() => {
        const isNarrative = checkNarativeTemplate();
        if (id === "intro-text-editor" && !isNarrative) {
          return null;
        }
        let showDropdown = false;
        let showInput = false;
        if (id === "intro-text-editor") {
          if (isNarrative) {
            showDropdown = true;
            showInput = true;
          }
        } else if (id === "commentText" || id === "") {
          if (isNarrative) {
            showDropdown = true;
            showInput = true;
          } else showDropdown = true;
        } else if (id === "summaryText") {
          if (isNarrative) {
            showDropdown = true;
            showInput = true;
          }
        } else if (id === "non-narrative-comment") {
          showDropdown = true;
          showInput = true;
        }
        if (!showDropdown && !showInput) return null;
        return (
          <Grid display="flex" className="add-dropdown-box">
            {showDropdown && (
              <>
                <Button
                  className="add-dropdown-typo"
                  id="add-dropdown"
                  onMouseDown={(e) => { e.preventDefault(); const ei = editor?.current?.editor; if (ei) { const old = ei.$el.get(0).querySelector('[data-cursor-bookmark]'); if (old) old.remove(); ei.html.insert('<span data-cursor-bookmark="1" contenteditable="false" style="display:none;font-size:0;"></span>'); } }}
                  onClick={(e) => handleButtonClicks(e, "global_lists")}
                  disableRipple
                  startIcon={<MenuBookIcon className="add-dropdown-circle" />}
                >
                  Global lists
                </Button>
                <Button
                  className="add-dropdown-typo"
                  id="add-dropdown"
                  onMouseDown={(e) => { e.preventDefault(); const ei = editor?.current?.editor; if (ei) { const old = ei.$el.get(0).querySelector('[data-cursor-bookmark]'); if (old) old.remove(); ei.html.insert('<span data-cursor-bookmark="1" contenteditable="false" style="display:none;font-size:0;"></span>'); } }}
                  onClick={(e) => handleButtonClicks(e, "new_list")}
                  disableRipple
                  startIcon={<AddCircleOutlineOutlinedIcon className="add-dropdown-circle" />}
                >
                  New list
                </Button>
              </>
            )}
            {showInput && (
              <Button
                className="add-dropdown-typo"
                id="add-input"
                onMouseDown={(e) => {
                  const editorInstance = editor?.current?.editor;
                  const editorRoot = editorInstance?.$el?.get(0);
                  const activeEl = document.activeElement;

                  if (editorRoot && activeEl?.tagName === "INPUT" && editorRoot.contains(activeEl)) {
                    const wrapper = activeEl.closest(".froala-dynamic-input-wrapper");
                    if (wrapper) {
                      const after = setRangeAfterWrapper(wrapper);
                      if (after) {
                        addInputClickRangeRef.current = after.cloneRange();
                        savedRangeRef.current = after.cloneRange();
                      }
                    }
                    e.preventDefault();
                    return;
                  }

                  const sel = window.getSelection();
                  if (editorRoot && sel?.rangeCount > 0) {
                    const range = sel.getRangeAt(0);
                    if (isRangeInEditor(range, editorRoot)) {
                      const cloned = normalizeRangeForLineBreakInsert(range.cloneRange());
                      addInputClickRangeRef.current = cloned;
                      savedRangeRef.current = cloned.cloneRange();
                    }
                  }
                  e.preventDefault();
                }}
                onClick={(event) => addInput(event)}
                disableRipple
                startIcon={<TextFieldsIcon className="add-dropdown-circle" />}
              >
                Add input
              </Button>
            )}
          </Grid>
        );
      })()}
      <EditOptionsDialog checkNarativeTemplate={checkNarativeTemplate} handleDeleteDropdown={handleDeleteDropdown} dialogRef={dialogRef} id={id} state={state} setState={setState} handleSave={handleSave}
        filterTagList={filterTagList} addDropdown={addDropdown} defaultCategories={storeData?.defaultCategories} categoryId={categoryId}
      />
      {
        openRemoveDropdown && <CopySummaryModal
          dialogRef={dialogRef}
          open={openRemoveDropdown}
          handleClose={handleCloseRemoveDropdown}
          handleSubmit={handleSubmitRemoveDropdown}
          submitButtonTitle={"Paste Without Dropdown"}
          dialogTitle={"Dropdown Won't Be Included"}
          dialogContentText={"Dropdowns cannot be copied and pasted. If you continue, only the text will be pasted without the dropdown."}
        />
      }
    </div >
  );
};

export default FroalaEditorComponent;